import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { toast, Toaster } from 'sonner';
import { Trophy, Target, Users, MessageCircle, ChevronRight, CheckCircle, Lock, Bot, ExternalLink, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// AlianzaGreen AI Assistant Button Component
const AlianzGreenAssistantButton = () => {
  const handleAssistantClick = () => {
    const assistantGPTUrl = "https://chatgpt.com/g/g-68791b2312bc8191a100c9b87ef92b91-marisa-green-experto-en-producto-y-redes";
    window.open(assistantGPTUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Button
        onClick={handleAssistantClick}
        className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-3"
        size="lg"
      >
        <Bot className="h-6 w-6" />
        <span className="font-medium">Asistente IA</span>
        <ExternalLink className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Catalog Access Button Component
const CatalogButton = () => {
  const handleCatalogClick = () => {
    const catalogUrl = "https://customer-assets.emergentagent.com/job_greenway-process/artifacts/ukt3sehv_Greenway_catalog_ARG_05.09.2025_compressed.pdf.pdf";
    window.open(catalogUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed bottom-6 right-80 z-50">
      <Button
        onClick={handleCatalogClick}
        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-3"
        size="lg"
      >
        <BookOpen className="h-6 w-6" />
        <span className="font-medium">Catálogo Oficial</span>
        <ExternalLink className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Price List Excel Button Component
const PriceListButton = () => {
  const handlePriceListClick = () => {
    const priceListUrl = "https://customer-assets-jt897jd0.emergentagent.net/job_fa573c8b-cfbd-48ca-ae1d-4768ba6f6487/artifacts/ohmbuw3v_lista-precios-septiembre-2026.xlsx";
    window.open(priceListUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed bottom-6 left-6 z-50">
      <Button
        onClick={handlePriceListClick}
        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-3"
        size="lg"
      >
        <span className="text-xl">📊</span>
        <span className="font-medium">Lista de Precios Septiembre</span>
        <ExternalLink className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Landing Page Component
const LandingPage = ({ onStartJourney }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/10 to-teal-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
          
          {/* Team Header */}
          <div className="text-center mb-12">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mx-auto max-w-4xl">
              <h1 className="text-2xl md:text-3xl font-bold mb-2">🌟 BIENVENIDOS A ALIANZ GREEN 🌟</h1>
              <p className="text-emerald-100 text-lg">Grupo de Líderes Asociados a tu Crecimiento</p>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Bienvenido a
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">AlianzaGreen</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Únete a cientos de distribuidores exitosos que han transformado sus vidas con el apoyo de 
                  <strong> AlianzaGreen</strong>, un grupo de líderes comprometidas con tu éxito en Greenway Global.
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-2xl border border-emerald-200">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-2xl">👥</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-900">AlianzaGreen</h3>
                    <p className="text-emerald-700">Líderes Unidas • Equipo de Éxito</p>
                  </div>
                </div>
                <p className="text-emerald-800 italic">
                  "No solo te enseñamos a vender productos, te enseñamos a construir un negocio que te dé libertad financiera 
                  y te permita vivir la vida que sueñas. Juntas somos más fuertes."
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={onStartJourney}
                  size="lg" 
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-6 py-3 text-base font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Unirme a AlianzaGreen
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-6 py-3 text-base font-medium rounded-xl transition-all duration-300"
                >
                  Conocer Más
                </Button>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-8 space-y-4 sm:space-y-0 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span>Capacitación Personalizada</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Trophy className="h-5 w-5 text-emerald-600" />
                  <span>Mentoría Directa</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Target className="h-5 w-5 text-emerald-600" />
                  <span>Resultados Comprobados</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRyYWluaW5nfGVufDB8fHx8MTc1NzM1Nzg0NHww&ixlib=rb-4.1.0&q=85"
                alt="Capacitación Empresarial"
                className="relative rounded-3xl shadow-2xl w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modules Preview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Tu Ruta de Crecimiento</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cada módulo está diseñado para llevarte paso a paso hacia el éxito, con objetivos claros y recompensas que celebran tu progreso.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { icon: Users, title: "Conociéndonos", description: "Conecta con nosotros y comparte tus sueños" },
            { icon: Target, title: "La Empresa", description: "Conoce la base sólida de Greenway Global" },
            { icon: Users, title: "Los Productos", description: "Conviértete en embajador de nuestra tecnología" }
          ].map((item, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900">{item.title}</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Videos Section */}
      <div className="bg-gradient-to-br from-emerald-900 via-teal-900 to-emerald-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Mira Nuestros Productos en Acción</h2>
            <p className="text-xl text-emerald-200 max-w-3xl mx-auto">
              Descubre el poder de la tecnología de microfibra Greenway
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Video 1 - La Mopa */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden shadow-2xl">
              <video 
                className="w-full aspect-video object-cover"
                controls
                poster=""
                preload="metadata"
              >
                <source src="https://customer-assets.emergentagent.com/job_alianz-training-1/artifacts/nyi0kmxi_cayo%20gaseosa.mp4" type="video/mp4" />
                Tu navegador no soporta videos.
              </video>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-2">LA MOPA</h3>
                <p className="text-emerald-200">Limpieza profesional para tu hogar sin químicos</p>
              </div>
            </div>
            
            {/* Video 2 - Cambiar tu Día */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden shadow-2xl">
              <video 
                className="w-full aspect-video object-cover"
                controls
                poster=""
                preload="metadata"
              >
                <source src="https://customer-assets.emergentagent.com/job_alianz-training-1/artifacts/q2zkgq3i_1000303932_286x508.mp4" type="video/mp4" />
                Tu navegador no soporta videos.
              </video>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-2">CAMBIAR TU DÍA</h3>
                <p className="text-emerald-200">Transforma tu rutina de limpieza diaria</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-8 px-8 rounded-2xl shadow-xl max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold mb-3">¿Listo para Empezar?</h3>
            <p className="text-emerald-100 mb-6">Únete a AlianzaGreen y comienza a ganar con productos de alta calidad</p>
            <Button 
              onClick={onStartJourney}
              size="lg" 
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-8 py-3 text-lg font-bold rounded-xl shadow-lg"
            >
              Unirme Ahora
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Component
const LoginForm = ({ onLogin }) => {
  const [formData, setFormData] = useState({ name: '', leader_name: '' });
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(true); // true = new registration, false = existing user login
  const [leaders, setLeaders] = useState([]);

  // Load authorized leaders for the dropdown
  useEffect(() => {
    axios.get(`${API}/leaders`)
      .then((res) => setLeaders(res.data))
      .catch(() => setLeaders([]));
  }, []);

  // Handle new user registration validated by authorized leader
  const handleNewUserSubmit = async (e) => {
    e.preventDefault();
    if (!formData.leader_name) {
      toast.error('Selecciona tu líder de la lista');
      return;
    }
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/users`, {
        name: formData.name,
        email: `${formData.name.toLowerCase().replace(/\s+/g, '.')}@alianzgreen.com`,
        leader_name: formData.leader_name
      });
      // Save to localStorage for session persistence
      localStorage.setItem('alianzgreen_user', JSON.stringify(response.data));
      onLogin(response.data);
      toast.success('¡Bienvenido a AlianzaGreen!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al registrarte. Verifica los datos ingresados.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle existing user login
  const handleExistingUserSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/users/login`, {
        name: formData.name,
        leader_name: formData.leader_name
      });
      // Save to localStorage for session persistence
      localStorage.setItem('alianzgreen_user', JSON.stringify(response.data));
      onLogin(response.data);
      toast.success('¡Bienvenido de nuevo!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Usuario no encontrado. Verifica tu nombre o regístrate.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      <Card className="w-full max-w-md bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto">
            <Users className="h-10 w-10 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            {isNewUser ? 'Unirme a AlianzaGreen' : 'Ya soy Asociado'}
          </CardTitle>
          <CardDescription className="text-gray-600">
            {isNewUser 
              ? 'Regístrate seleccionando el nombre de tu líder' 
              : 'Ingresa tu nombre para continuar tu capacitación'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Toggle buttons */}
          <div className="flex gap-2 mb-6">
            <Button
              type="button"
              variant={isNewUser ? "default" : "outline"}
              data-testid="mode-new-user-button"
              className={`flex-1 ${isNewUser ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              onClick={() => setIsNewUser(true)}
            >
              🆕 Soy Nuevo
            </Button>
            <Button
              type="button"
              variant={!isNewUser ? "default" : "outline"}
              data-testid="mode-existing-user-button"
              className={`flex-1 ${!isNewUser ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              onClick={() => setIsNewUser(false)}
            >
              🔄 Ya tengo cuenta
            </Button>
          </div>

          {isNewUser ? (
            // New user registration form
            <form onSubmit={handleNewUserSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name">👤 Tu Nombre Completo</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  placeholder="Tu nombre completo"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="leader_select">👑 Selecciona tu Líder/Patrocinador</Label>
                <select
                  id="leader_select"
                  data-testid="register-leader-select"
                  value={formData.leader_name}
                  onChange={(e) => setFormData({ ...formData, leader_name: e.target.value })}
                  className="w-full h-12 px-3 rounded-md border border-gray-200 bg-white text-gray-900 focus:border-emerald-500 focus:ring-emerald-500 focus:outline-none"
                  required
                >
                  <option value="">-- Elige a tu líder --</option>
                  {leaders.map((leader) => (
                    <option key={leader.id} value={leader.name}>{leader.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 text-center">¿Quién te invitó a AlianzaGreen?</p>
              </div>
              <Button
                type="submit"
                data-testid="register-submit-button"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {loading ? 'Registrando...' : 'Registrarme'}
              </Button>
            </form>
          ) : (
            // Existing user login form
            <form onSubmit={handleExistingUserSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name_login">👤 Tu Nombre Completo</Label>
                <Input
                  id="name_login"
                  data-testid="login-name-input"
                  type="text"
                  placeholder="El mismo nombre con el que te registraste"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                  required
                />
                <p className="text-xs text-gray-500">Escribe tu nombre exactamente como lo registraste</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="leader_name_login">👑 Nombre de tu Líder</Label>
                <Input
                  id="leader_name_login"
                  data-testid="login-leader-input"
                  type="text"
                  placeholder="Nombre de tu líder"
                  value={formData.leader_name}
                  onChange={(e) => setFormData({ ...formData, leader_name: e.target.value })}
                  className="h-12 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {loading ? 'Buscando tu cuenta...' : 'Entrar a mi cuenta'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Panel Component (Super Admin + Panel de Líderes)
const AdminPanel = () => {
  const [role, setRole] = useState(null); // null | 'super' | 'leader'
  const [loginMode, setLoginMode] = useState('super');
  const [password, setPassword] = useState('');
  const [leaderCreds, setLeaderCreds] = useState({ name: '', password: '' });
  const [leaderInfo, setLeaderInfo] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [stats, setStats] = useState(null);
  const [newLeader, setNewLeader] = useState({ name: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSuperLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setRole('super');
      loadSuperData(password);
      toast.success('Acceso autorizado');
    } catch (error) {
      toast.error('Contraseña incorrecta');
    }
  };

  const handleLeaderLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/leader/login`, leaderCreds);
      setLeaderInfo(res.data);
      setRole('leader');
      loadLeaderData();
      toast.success(`¡Hola ${res.data.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    }
  };

  const loadSuperData = async (pwd = password) => {
    setLoading(true);
    try {
      const [leadersRes, usersRes, statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/leaders?password=${encodeURIComponent(pwd)}`),
        axios.get(`${API}/admin/users?password=${encodeURIComponent(pwd)}`),
        axios.get(`${API}/admin/stats?password=${encodeURIComponent(pwd)}`),
        axios.get(`${API}/admin/leads?password=${encodeURIComponent(pwd)}`)
      ]);
      setLeaders(leadersRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leader/associates`, leaderCreds);
      setAssociates(res.data);
    } catch (error) {
      toast.error('Error al cargar tus socios');
    } finally {
      setLoading(false);
    }
  };

  const createLeader = async (e) => {
    e.preventDefault();
    if (!newLeader.name.trim() || !newLeader.password.trim()) {
      toast.error('Completa el nombre y la contraseña del líder');
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/leaders?password=${encodeURIComponent(password)}`, newLeader);
      toast.success(res.data.message);
      setNewLeader({ name: '', password: '' });
      loadSuperData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar líder');
    }
  };

  const toggleLeader = async (leaderId) => {
    try {
      const res = await axios.put(`${API}/admin/leaders/${leaderId}/toggle?password=${encodeURIComponent(password)}`);
      toast.success(res.data.message);
      loadSuperData();
    } catch (error) {
      toast.error('Error al cambiar el estado del líder');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
      return '-';
    }
  };

  if (!role) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔐</span>
            </div>
            <CardTitle className="text-2xl font-bold">Panel de Administración</CardTitle>
            <p className="text-gray-500">AlianzaGreen - Acceso Restringido</p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-6">
              <Button
                type="button"
                data-testid="admin-mode-super-button"
                variant={loginMode === 'super' ? 'default' : 'outline'}
                className={`flex-1 ${loginMode === 'super' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                onClick={() => setLoginMode('super')}
              >
                👑 Super Admin
              </Button>
              <Button
                type="button"
                data-testid="admin-mode-leader-button"
                variant={loginMode === 'leader' ? 'default' : 'outline'}
                className={`flex-1 ${loginMode === 'leader' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                onClick={() => setLoginMode('leader')}
              >
                🤝 Soy Líder
              </Button>
            </div>

            {loginMode === 'super' ? (
              <form onSubmit={handleSuperLogin} className="space-y-4">
                <div>
                  <Label>Contraseña</Label>
                  <Input
                    type="password"
                    data-testid="admin-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Ingresa tu contraseña"
                    className="h-12"
                    required
                  />
                </div>
                <Button type="submit" data-testid="admin-login-button" className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600">
                  Ingresar
                </Button>
              </form>
            ) : (
              <form onSubmit={handleLeaderLogin} className="space-y-4">
                <div>
                  <Label>Tu Nombre de Líder</Label>
                  <Input
                    type="text"
                    data-testid="leader-name-input"
                    value={leaderCreds.name}
                    onChange={(e) => setLeaderCreds({ ...leaderCreds, name: e.target.value })}
                    placeholder="Nombre registrado por el administrador"
                    className="h-12"
                    required
                  />
                </div>
                <div>
                  <Label>Contraseña</Label>
                  <Input
                    type="password"
                    data-testid="leader-password-input"
                    value={leaderCreds.password}
                    onChange={(e) => setLeaderCreds({ ...leaderCreds, password: e.target.value })}
                    placeholder="Tu contraseña de líder"
                    className="h-12"
                    required
                  />
                </div>
                <Button type="submit" data-testid="leader-login-button" className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600">
                  Entrar a mi Panel
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Leader View ----
  if (role === 'leader') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 rounded-2xl mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold" data-testid="leader-panel-title">🤝 Panel de Líder</h1>
              <p className="text-emerald-100">Hola, {leaderInfo?.name}</p>
            </div>
            <Button
              data-testid="leader-logout-button"
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white/20"
              onClick={() => { setRole(null); setLeaderInfo(null); setAssociates([]); setLeaderCreds({ name: '', password: '' }); }}
            >
              Salir
            </Button>
          </div>

          <Card className="p-6 text-center mb-6">
            <p className="text-5xl font-bold text-emerald-600" data-testid="leader-associates-count">{associates.length}</p>
            <p className="text-gray-600 mt-2">Socios registrados contigo</p>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>👥 Mis Socios</CardTitle>
              <Button onClick={loadLeaderData} variant="outline" data-testid="leader-refresh-button">🔄 Actualizar</Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-gray-500">Cargando...</p>
              ) : associates.length === 0 ? (
                <p className="text-center py-8 text-gray-500" data-testid="leader-no-associates">Aún no tienes socios registrados. Comparte tu nombre con tus invitados para que se registren.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="leader-associates-table">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="p-3 text-left font-semibold">Nombre</th>
                        <th className="p-3 text-left font-semibold">Progreso</th>
                        <th className="p-3 text-left font-semibold">Fecha de Registro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {associates.map((assoc, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{assoc.name}</td>
                          <td className="p-3">
                            <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-sm">
                              {assoc.completed_modules} / 11 módulos
                            </span>
                          </td>
                          <td className="p-3 text-gray-600">{formatDate(assoc.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---- Super Admin View ----
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 rounded-2xl mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" data-testid="admin-panel-title">👑 Panel de Administración</h1>
            <p className="text-emerald-100">Gestiona los líderes autorizados de tu equipo</p>
          </div>
          <Button
            data-testid="admin-logout-button"
            variant="outline"
            className="bg-white/10 text-white border-white/40 hover:bg-white/20"
            onClick={() => { setRole(null); setPassword(''); setLeaders([]); setUsers([]); setLeads([]); setStats(null); }}
          >
            Salir
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600" data-testid="stat-active-leaders">{stats.active_leaders}</p>
              <p className="text-sm text-gray-600">Líderes Activos</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600" data-testid="stat-total-leaders">{stats.total_leaders}</p>
              <p className="text-sm text-gray-600">Total Líderes</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-3xl font-bold text-orange-600" data-testid="stat-total-users">{stats.total_users}</p>
              <p className="text-sm text-gray-600">Socios Registrados</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600" data-testid="stat-total-leads">{stats.total_leads || 0}</p>
              <p className="text-sm text-gray-600">Prospectos</p>
            </Card>
          </div>
        )}

        {/* Add Leader */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>➕ Agregar Líder Autorizado</CardTitle>
            <p className="text-sm text-gray-500">Los socios nuevos solo podrán registrarse eligiendo un líder de esta lista</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={createLeader} className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label>Nombre del Líder</Label>
                <Input
                  data-testid="new-leader-name-input"
                  value={newLeader.name}
                  onChange={(e) => setNewLeader({ ...newLeader, name: e.target.value })}
                  placeholder="Ej: María López"
                  className="h-11"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label>Contraseña del Líder</Label>
                <Input
                  data-testid="new-leader-password-input"
                  type="text"
                  value={newLeader.password}
                  onChange={(e) => setNewLeader({ ...newLeader, password: e.target.value })}
                  placeholder="Contraseña para su panel"
                  className="h-11"
                />
              </div>
              <Button type="submit" data-testid="create-leader-button" className="bg-emerald-600 hover:bg-emerald-700 h-11">
                Agregar Líder
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Leaders List */}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>👑 Líderes Autorizados</CardTitle>
            <Button onClick={() => loadSuperData()} variant="outline" data-testid="admin-refresh-button">🔄 Actualizar</Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Cargando...</p>
            ) : leaders.length === 0 ? (
              <p className="text-center py-8 text-gray-500" data-testid="no-leaders-message">No hay líderes registrados. ¡Agrega el primero!</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leaders-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="p-3 text-left font-semibold">Líder</th>
                      <th className="p-3 text-left font-semibold">Socios</th>
                      <th className="p-3 text-left font-semibold">Estado</th>
                      <th className="p-3 text-left font-semibold">Agregado</th>
                      <th className="p-3 text-left font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaders.map((leader) => (
                      <tr key={leader.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{leader.name}</td>
                        <td className="p-3">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">{leader.associates_count}</span>
                        </td>
                        <td className="p-3">
                          {leader.active ? (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">Activo</span>
                          ) : (
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm">Desactivado</span>
                          )}
                        </td>
                        <td className="p-3 text-gray-600">{formatDate(leader.created_at)}</td>
                        <td className="p-3">
                          <Button
                            onClick={() => toggleLeader(leader.id)}
                            data-testid={`toggle-leader-${leader.id}`}
                            variant="outline"
                            size="sm"
                            className={leader.active ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}
                          >
                            {leader.active ? 'Desactivar' : 'Activar'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>👥 Socios Registrados</CardTitle>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No hay socios registrados todavía.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="p-3 text-left font-semibold">Nombre</th>
                      <th className="p-3 text-left font-semibold">Líder</th>
                      <th className="p-3 text-left font-semibold">Progreso</th>
                      <th className="p-3 text-left font-semibold">Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{user.name}</td>
                        <td className="p-3 text-gray-600">{user.leader_name || '-'}</td>
                        <td className="p-3">
                          <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-sm">
                            {user.completed_modules} / 11
                          </span>
                        </td>
                        <td className="p-3 text-gray-600">{formatDate(user.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leads List */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>📩 Prospectos de la Landing</CardTitle>
            <p className="text-sm text-gray-500">Personas que completaron el formulario de contacto</p>
          </CardHeader>
          <CardContent>
            {leads.length === 0 ? (
              <p className="text-center py-8 text-gray-500" data-testid="no-leads-message">Todavía no hay prospectos guardados.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leads-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="p-3 text-left font-semibold">Nombre</th>
                      <th className="p-3 text-left font-semibold">WhatsApp</th>
                      <th className="p-3 text-left font-semibold">Email</th>
                      <th className="p-3 text-left font-semibold">Ciudad</th>
                      <th className="p-3 text-left font-semibold">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{lead.nombre}</td>
                        <td className="p-3">
                          <a
                            href={`https://wa.me/${(lead.whatsapp || '').replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:underline"
                          >
                            {lead.whatsapp}
                          </a>
                        </td>
                        <td className="p-3 text-gray-600">{lead.email || '-'}</td>
                        <td className="p-3 text-gray-600">{lead.ciudad || '-'}</td>
                        <td className="p-3 text-gray-600">{formatDate(lead.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Module Dashboard Component
const ModuleDashboard = ({ modules, userProfile, user }) => {
  const navigate = useNavigate();
  const completedCount = userProfile?.completed_modules?.length || 0;
  const progressPercentage = (completedCount / modules.length) * 100;

  const handleLogout = () => {
    localStorage.removeItem('alianzgreen_user');
    window.location.href = '/socios';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      <PriceListButton />
      
      {/* Header */}
      <div className="text-center mb-12">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mx-auto max-w-4xl mb-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-emerald-100 text-sm" data-testid="dashboard-user-greeting">👋 Hola, {user?.name}</p>
            <Button
              data-testid="dashboard-logout-button"
              variant="outline"
              size="sm"
              className="bg-white/10 text-white border-white/40 hover:bg-white/20"
              onClick={handleLogout}
            >
              Cerrar sesión
            </Button>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">🌟 ALIANZ GREEN 🌟</h1>
          <p className="text-emerald-100 text-lg">Tu Dashboard de Capacitación</p>
        </div>
        
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Tu Camino de Crecimiento</h2>
        <p className="text-xl text-gray-600 mb-8">Progreso: {completedCount} de {modules.length} módulos completados</p>
        
        <div className="max-w-md mx-auto mb-8">
          <Progress value={progressPercentage} className="h-4" />
          <p className="text-sm text-gray-500 mt-2">{Math.round(progressPercentage)}% completado</p>
        </div>
      </div>

      {/* Modules Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {modules.map((module) => {
          const isUnlocked = module.id === 0 || userProfile?.completed_modules?.includes(module.id - 1);
          const isCompleted = userProfile?.completed_modules?.includes(module.id);
          
          return (
            <Card
              key={module.id}
              className={`transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm ${
                isUnlocked 
                  ? 'hover:shadow-xl hover:scale-105' 
                  : 'opacity-60'
              } ${isCompleted ? 'ring-2 ring-emerald-200' : ''}`}
            >
              <CardHeader className="text-center pb-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform duration-300 ${
                  isUnlocked ? 'group-hover:scale-110' : ''
                } ${
                  isCompleted 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
                    : isUnlocked
                    ? 'bg-gradient-to-r from-gray-400 to-gray-500'
                    : 'bg-gray-300'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="h-8 w-8 text-white" />
                  ) : isUnlocked ? (
                    <Target className="h-8 w-8 text-white" />
                  ) : (
                    <Lock className="h-8 w-8 text-gray-500" />
                  )}
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-500">Módulo {module.id}</span>
                  {isCompleted && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                </div>
                <CardTitle className="text-xl font-bold text-gray-900">{module.title}</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">{module.objective}</p>
                {module.medal && (
                  <Badge variant="outline" className="mt-3 text-emerald-700 border-emerald-200">
                    <Trophy className="h-3 w-3 mr-1" />
                    {module.medal}
                  </Badge>
                )}
                <div className="mt-4">
                  {isUnlocked ? (
                    <Button
                      onClick={() => navigate(`/module/${module.id}`)}
                      className={`w-full ${
                        isCompleted 
                          ? 'bg-emerald-500 hover:bg-emerald-600' 
                          : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
                      }`}
                    >
                      {isCompleted ? '✓ Revisar' : 'Entrar'} <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button disabled className="w-full bg-gray-300 cursor-not-allowed">
                      <Lock className="mr-2 h-4 w-4" /> Bloqueado
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// Module 0: Conociéndonos
const Module0 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);
  const [formData, setFormData] = useState({
    dreams: '',
    current_income: '',
    available_time: '',
    motivation_level: 5,
    company_id: ''
  });

  const handleComplete = async () => {
    console.log('handleComplete called, user:', user);
    if (!user || !user.id) {
      console.error('User or user.id is missing');
      toast.error('Error: No se encontró el usuario. Por favor, vuelve a iniciar sesión.');
      return;
    }
    try {
      await axios.put(`${API}/users/${user.id}/dreams`, formData);
      await axios.post(`${API}/users/${user.id}/complete-module/0`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Primer paso dado');
      navigate('/');
    } catch (error) {
      console.error('Error completing module:', error);
      toast.error('Error al guardar. Intenta nuevamente.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">🤝 Módulo 0: Conociéndonos</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: Bienvenida */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">¡Bienvenido/a!</h2>
            
            {/* Video de Bienvenida */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border-2 border-emerald-200">
              <div className="text-center mb-4">
                <span className="text-4xl">🎬</span>
                <h3 className="text-xl font-bold text-emerald-900 mt-2">Video de Bienvenida</h3>
              </div>
              <div className="aspect-video w-full max-w-2xl mx-auto rounded-xl overflow-hidden shadow-lg">
                <video 
                  controls 
                  className="w-full h-full"
                  poster=""
                >
                  <source src="https://customer-assets.emergentagent.com/job_alianz-training/artifacts/4xi3tk06_BIENVENIDA.mp4" type="video/mp4" />
                  Tu navegador no soporta videos HTML5.
                </video>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              Somos <strong>AlianzaGreen</strong>, un grupo de líderes comprometidas con tu crecimiento. 
              Estamos aquí para acompañarte paso a paso hacia tus metas.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              No estás solo/a en esto, somos un equipo unido y listo para apoyarte. 
              Este programa te llevará paso a paso hacia tus metas. <strong>¡Empecemos juntas!</strong>
            </p>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-300">
              <div className="flex items-start space-x-4">
                <div className="text-3xl">🔑</div>
                <div>
                  <h3 className="text-lg font-bold text-blue-900 mb-2">Tu Acceso a Greenway</h3>
                  <p className="text-gray-700">
                    Tú recibiste un <strong>código de identificación</strong> y una <strong>contraseña</strong> para 
                    entrar a tu página web personal y desarrollar desde allí tu negocio. ¡Guárdalos bien!
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Formulario */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">Cuéntanos sobre ti</h2>
            <p className="text-gray-600">Tus respuestas nos ayudarán a personalizar tu experiencia</p>
            
            <div className="space-y-4">
              <div>
                <Label>¿Cuál es tu mayor sueño o meta?</Label>
                <textarea
                  value={formData.dreams}
                  onChange={(e) => setFormData({...formData, dreams: e.target.value})}
                  className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  rows="3"
                  placeholder="Ej: Tener libertad financiera, viajar con mi familia..."
                />
              </div>
              
              <div>
                <Label>¿Cuánto ganas actualmente al mes?</Label>
                <select
                  value={formData.current_income}
                  onChange={(e) => setFormData({...formData, current_income: e.target.value})}
                  className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Menos de $200k">Menos de $200k</option>
                  <option value="$200k-500k">$200k - $500k</option>
                  <option value="$500k-1M">$500k - $1M</option>
                  <option value="Más de 1M">Más de $1M</option>
                </select>
              </div>
              
              <div>
                <Label>¿Cuántas horas a la semana puedes dedicar?</Label>
                <select
                  value={formData.available_time}
                  onChange={(e) => setFormData({...formData, available_time: e.target.value})}
                  className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Selecciona una opción</option>
                  <option value="2-5 horas">2-5 horas</option>
                  <option value="5-10 horas">5-10 horas</option>
                  <option value="10-20 horas">10-20 horas</option>
                  <option value="20+ horas">20+ horas</option>
                </select>
              </div>
              
              <div>
                <Label>Del 1 al 10, ¿qué tan motivado/a estás?</Label>
                <div className="flex items-center gap-4 mt-2">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.motivation_level}
                    onChange={(e) => setFormData({...formData, motivation_level: parseInt(e.target.value)})}
                    className="flex-1"
                  />
                  <span className="text-2xl font-bold text-emerald-600">{formData.motivation_level}</span>
                </div>
              </div>
              
              <div>
                <Label>Ingresa tu ID de distribuidor Greenway</Label>
                <Input
                  value={formData.company_id}
                  onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                  placeholder="Tu ID de distribuidor"
                  className="mt-2"
                />
              </div>
            </div>
            
            <div className="flex justify-between">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Mensaje Motivador */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6 text-center">
            <div className="text-6xl mb-4">🚀</div>
            <h2 className="text-3xl font-bold text-gray-900">¡Tu Camino Comienza Aquí!</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              ¡Excelente, <strong>{user.name}</strong>! Tu sueño es: <em>"{formData.dreams || 'alcanzar el éxito'}"</em>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Recuerda esto cada día. Este programa está diseñado para que entiendas completamente 
              el negocio y empieces a ver resultados.
            </p>
            <p className="text-2xl font-bold text-emerald-600">¡Tu futuro empieza HOY!</p>
            
            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Primer paso dado</p>
            </div>
            
            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 1: ¿Qué es Greenway?
const Module1 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/1`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Conozco mi empresa');
      navigate('/');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">🏢 Módulo 1: ¿Qué es Greenway?</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: Historia de Greenway */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">La Historia de Greenway Global</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Greenway nació en 1997 en Rusia con una misión clara: <strong className="text-emerald-700">reducir 
                plásticos y químicos tóxicos en el uso cotidiano</strong>. Los fundadores vieron que nuestros hogares 
                estaban llenos de productos dañinos que afectaban nuestra salud y el planeta.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                La solución fue la tecnología <strong>Up Poly</strong>, una patente exclusiva que permite crear productos 
                seguros y efectivos sin plásticos ni químicos tóxicos. Desde 1997, Greenway creció hasta estar presente 
                en <strong className="text-emerald-700">más de 60 países</strong> y seguimos en expansión constante.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Hoy somos más que una empresa: somos una comunidad global de distribuidores y familias comprometidas 
                con vivir mejor, cuidar nuestra salud y proteger el planeta.
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Misión y Valores */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Lo que nos mueve como empresa</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200 mb-6">
                <p className="text-xl text-emerald-900 leading-relaxed italic">
                  "Reducir plásticos y químicos tóxicos en el uso cotidiano, transformando hogares 
                  en espacios más saludables y sostenibles."
                </p>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                Creemos en la <strong>innovación</strong> que cuida a las personas y al planeta. La tecnología Up Poly 
                permite crear productos efectivos sin plásticos ni químicos dañinos, resultado de años de investigación 
                y compromiso con la salud y el medio ambiente.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Somos una <strong>comunidad</strong> global. Cada distribuidor no solo construye un negocio, forma parte 
                de un movimiento que cambia vidas. Crecemos juntos, nos apoyamos mutuamente y celebramos cada éxito 
                como un logro colectivo.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Nuestro <strong>impacto global</strong> es real: más de 60 países, miles de distribuidores y millones 
                de familias que mejoraron su calidad de vida. Y esto es solo el comienzo.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                La <strong>excelencia</strong> es innegociable. Cada producto cumple con los más altos estándares 
                internacionales. Cuando eliges Greenway, eliges calidad y un futuro mejor.
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Greenway en el Mundo */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Greenway en el Mundo</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Lo que empezó en Rusia en 1997 hoy está en <strong className="text-emerald-700">más de 60 países</strong> 
                en América, Europa, Asia y Oceanía. Millones de hogares han descubierto que es posible vivir más saludable 
                sin sacrificar calidad.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Lo extraordinario es nuestra comunidad: miles de distribuidores de diferentes países y culturas, unidos 
                por la misma misión. Cada uno es un embajador de cambio demostrando que existe una alternativa real a 
                los productos llenos de químicos.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Seguimos en expansión constante. Cada mes llegamos a nuevos mercados y familias. Por cada producto vendido, 
                reducimos plásticos y químicos tóxicos. Por cada nuevo distribuidor, creamos una oportunidad de ingresos 
                y libertad financiera.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Como distribuidor, tu alcance no tiene fronteras. Tu link funciona en más de 60 países. Puedes construir 
                un negocio global desde tu hogar. <strong>Las oportunidades son infinitas.</strong>
              </p>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Conozco mi empresa</p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 2: La Tecnología - Catálogo y Productos
const Module2 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/2`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Conozco los productos');
      navigate('/');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al completar módulo');
    }
  };

  const handleCatalogClick = () => {
    const catalogUrl = "https://customer-assets.emergentagent.com/job_greenway-process/artifacts/ukt3sehv_Greenway_catalog_ARG_05.09.2025_compressed.pdf.pdf";
    window.open(catalogUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">🔬 Módulo 2: La Tecnología - Catálogo y Productos</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: Tecnología Up Poly */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">La Revolución de las Fibras Inteligentes</h2>
            
            {/* Video de Tipos de Fibras */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-300">
              <div className="text-center mb-4">
                <span className="text-4xl">🎬</span>
                <h3 className="text-xl font-bold text-blue-900 mt-2">Video: Tipos de Fibras y Niveles</h3>
                <p className="text-gray-600">Absorción, pulido y abrasión explicados</p>
              </div>
              <div className="aspect-video w-full max-w-2xl mx-auto rounded-xl overflow-hidden shadow-lg bg-black">
                <video 
                  controls 
                  className="w-full h-full"
                >
                  <source src="https://customer-assets.emergentagent.com/job_alianz-training/artifacts/se7g6lc9_LA%20TECNOLGIA%20TIPO%20DE%20FIBRAS.mp4" type="video/mp4" />
                  Tu navegador no soporta videos HTML5.
                </video>
              </div>
            </div>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong className="text-emerald-700">Up Poly</strong> es nuestra tecnología patentada que revoluciona 
                la limpieza del hogar. Es una fibra especial que combina materiales naturales de alta calidad para crear 
                paños ultra efectivos, sin necesidad de químicos tóxicos ni plásticos dañinos.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                A diferencia de los paños convencionales, los paños con tecnología Up Poly son biodegradables, 
                reutilizables y completamente seguros para toda la familia. Limpian de forma profunda usando solo agua, 
                eliminando hasta el 99% de bacterias y suciedad sin contaminar tu hogar ni el medio ambiente.
              </p>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200 mt-6">
                <h3 className="text-xl font-bold text-emerald-900 mb-4">✨ Beneficios de Up Poly:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2 text-xl">🦠</span>
                    <span><strong>Anti bacterianos:</strong> No dejan que la bacteria se reproduzca, por lo que muere naturalmente</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2 text-xl">✨</span>
                    <span><strong>Sin olores ni babosidades:</strong> Permanecen frescos y limpios sin generar mal olor</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2 text-xl">⏰</span>
                    <span><strong>Duran más de 2 años:</strong> Inversión a largo plazo, no los reemplazas cada mes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2 text-xl">💧</span>
                    <span><strong>Solo con agua:</strong> No necesitas químicos para limpiar, solo agua</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2 text-xl">🧼</span>
                    <span><strong>Limpieza del paño:</strong> Para lavar los paños usa solo jabón blanco o detergentes naturales no químicos</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Ver Catálogo <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Catálogo de Productos */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Explora Nuestro Catálogo</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Greenway ofrece más de 800 productos diseñados para mejorar tu vida diaria. Desde productos para el hogar 
                hasta cuidado personal, cada artículo está desarrollado con tecnología Up Poly para garantizar seguridad, 
                efectividad y respeto por el medio ambiente.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Nuestro catálogo está en constante crecimiento. Cada mes lanzamos nuevos productos basados en las necesidades 
                de nuestros clientes y las últimas innovaciones en tecnología textil y química ecológica.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl border border-blue-200 text-center">
              <BookOpen className="h-16 w-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-blue-900 mb-3">Catálogo Oficial Greenway</h3>
              <p className="text-blue-800 mb-6">
                Descarga nuestro catálogo completo y descubre todos los productos disponibles con precios y descripciones detalladas.
              </p>
              <Button
                onClick={handleCatalogClick}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3"
                size="lg"
              >
                📥 Descargar Catálogo PDF
              </Button>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Tipos de Productos */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Familias de Productos Greenway</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Nuestro catálogo está organizado en familias de productos para que encuentres fácilmente lo que necesitas. 
                Cada categoría ofrece soluciones específicas sin comprometer tu salud ni el medio ambiente.
              </p>
            </div>

            <div className="space-y-4 mt-6">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200">
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">🧽</div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-900 mb-2">Paños de Limpieza Up Poly</h3>
                    <p className="text-gray-700">
                      Paños de microfibra con tecnología Up Poly que limpian profundamente solo con agua. 
                      Para cocina, baño, vidrios, pisos y toda superficie. Reutilizables y ultra resistentes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200">
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">✨</div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-900 mb-2">Fibras para Cuidado Personal</h3>
                    <p className="text-gray-700">
                      Fibras especiales con tecnología Up Poly diseñadas para cuidar tu piel de forma natural 
                      y efectiva, sin químicos dañinos.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200">
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">🪣</div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-900 mb-2">Accesorios de Limpieza</h3>
                    <p className="text-gray-700">
                      Trapeadores y baldes diseñados para facilitar la limpieza del hogar de forma práctica 
                      y eficiente.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">🚗</div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-900 mb-2">Productos para Autos</h3>
                    <p className="text-gray-700">
                      Paños especiales con tecnología Up Poly para limpiar y pulir tu auto sin rayar. 
                      Para carrocería, vidrios, tablero e interiores. Resultados profesionales en casa.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-xl border border-orange-200">
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">🐾</div>
                  <div>
                    <h3 className="text-xl font-bold text-orange-900 mb-2">Productos para Mascotas</h3>
                    <p className="text-gray-700">
                      Fibras especiales para el cuidado e higiene de tus mascotas. Limpieza suave y segura 
                      para perros y gatos, sin químicos que puedan dañarlos.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <p className="text-lg text-gray-800 text-center">
                <strong>💡 Recuerda:</strong> Solo recomiendas lo que conoces. En el próximo módulo aprenderás 
                cómo probar los productos y generar tu testimonio.
              </p>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Conozco los productos</p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 3: Sistema de Puntos y Activación
const Module3 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/3`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Ya estoy activo');
      navigate('/');
    } catch (error) {
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">💰 Módulo 3: Sistema de Puntos y Activación</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: PV y UC Explicados */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">¿Qué son PV y UC?</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                En Greenway trabajamos con dos conceptos fundamentales: <strong className="text-emerald-700">PV (Puntos de Volumen)</strong> 
                y <strong className="text-emerald-700">UV (Unidad de Valor)</strong>.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Todos los productos tienen un valor en <strong>PV</strong>, que es la manera de unificar el valor 
                a nivel mundial. En Argentina, este valor es <strong className="text-emerald-700">1530 PV</strong>. 
                El <strong>UV</strong> es la unidad de cobro, que en Argentina equivale a <strong className="text-emerald-700">1030 UV</strong>.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-2xl font-bold text-blue-900 mb-3">📊 PV - Puntos de Volumen</h3>
                <p className="text-blue-800 mb-3">
                  Es el sistema de unificación mundial de Greenway. Permite que todos los distribuidores 
                  del mundo trabajen con la misma referencia de valor.
                </p>
                <div className="bg-white p-4 rounded-lg mt-3">
                  <p className="text-sm text-gray-600 mb-2">En Argentina:</p>
                  <p className="text-gray-800 text-center">
                    <strong className="text-blue-700 text-2xl">1530 PV</strong>
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200">
                <h3 className="text-2xl font-bold text-emerald-900 mb-3">💵 UV - Unidad de Valor</h3>
                <p className="text-emerald-800 mb-3">
                  Es la unidad de cobro local. Representa el valor económico real en tu país para 
                  calcular precios y comisiones.
                </p>
                <div className="bg-white p-4 rounded-lg mt-3">
                  <p className="text-sm text-gray-600 mb-2">En Argentina:</p>
                  <p className="text-gray-800 text-center">
                    <strong className="text-emerald-700 text-2xl">1030 UV</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <p className="text-lg text-gray-800">
                <strong>💡 Punto clave:</strong> El sistema PV permite que Greenway opere globalmente con 
                una referencia unificada, mientras que UV se adapta a la economía local de cada país.
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Activación Mínima 50 PV */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Activación Mínima: 50 PV</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Para estar activo como distribuidor y recibir comisiones, necesitas comprar un mínimo de <strong className="text-emerald-700">50 PV al mes</strong>. 
                Esto no es un gasto, es tu inversión en el negocio que te permite generar ingresos ilimitados.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Estos 50 PV equivalen aproximadamente a productos que usarás en tu propio hogar: paños de limpieza, 
                productos de higiene, limpiadores concentrados. No necesitas comprar stock para vender, solo lo que 
                usas para ti y tu familia.
              </p>
            </div>

            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200 mt-6">
              <h3 className="text-xl font-bold text-emerald-900 mb-4">✅ Beneficios de estar activo (50 PV/mes):</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>Recibes comisiones del 35%</strong> en tu billetera por ventas directas</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>Puedes armar tu red</strong> de distribuidores y ganar comisiones de tu equipo</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>Calificas para bonos</strong> adicionales según tu rango</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>Tu link de venta está activo</strong> en más de 60 países</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>Acceso a capacitaciones</strong> y eventos exclusivos</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mt-6">
              <p className="text-lg text-blue-900 mb-3">
                <strong>💡 Recomendación:</strong> Empieza con productos que uses tú mismo. Tu testimonio personal 
                es tu mejor herramienta de venta. Nadie recomienda mejor que quien ya probó y le funcionó.
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200 mt-6">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <div className="text-3xl">🚀</div>
                <h3 className="text-xl font-bold text-purple-900">¿Quieres dar velocidad a tu negocio?</h3>
              </div>
              <p className="text-lg text-purple-900 text-center">
                <strong>Te recomendamos iniciar con el Turbo Pack.</strong> Obtienes $230,000 para recompra 
                de productos que nunca se vencen y puedes retirar hasta el 50% de tu compra en productos.
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Ver Turbo Pack <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Turbo Pack */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Turbo Pack: Tu Mejor Inicio</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                El <strong className="text-emerald-700">Turbo Pack</strong> es la forma más inteligente de comenzar 
                tu negocio en Greenway. Es un paquete especial de 200 PV diseñado para darte un arranque acelerado 
                con beneficios exclusivos.
              </p>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50 p-8 rounded-xl border-4 border-yellow-400 mt-6">
              <div className="text-center mb-6">
                <div className="text-5xl mb-3">🚀</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">TURBO PACK</h3>
                <p className="text-emerald-700 text-2xl font-bold">200 PV</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl mb-4">
                <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">🎁 Beneficio Especial:</h4>
                <p className="text-center text-3xl font-bold text-emerald-600 mb-2">$230,000 ARS</p>
                <p className="text-center text-gray-700 leading-relaxed">
                  Para recompra de productos a partir del siguiente período
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200 mt-6">
              <h3 className="text-xl font-bold text-emerald-900 mb-4">✨ Beneficios del Turbo Pack:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>$230,000 para recompras:</strong> Úsalo en futuras compras de productos</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>Disponible desde el siguiente período:</strong> Lo puedes usar inmediatamente después</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>No se vence:</strong> Úsalo cuando lo necesites, sin presión de tiempo</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>Retira hasta el 50%:</strong> Puedes retirar hasta la mitad de tu compra en productos</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span><strong>Arranque acelerado:</strong> Empiezas con 200 PV para activarte fuerte</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mt-6">
              <p className="text-lg text-blue-900 text-center">
                <strong>💼 El Turbo Pack es la elección de los distribuidores exitosos.</strong> Da velocidad 
                a tu negocio con un beneficio que no caduca y máxima flexibilidad.
              </p>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Ya estoy activo</p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 4: Prueba los Productos
const Module4 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/4`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Embajador del producto');
      navigate('/');
    } catch (error) {
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">🧪 Módulo 4: Prueba los Productos</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: Nadie recomienda lo que no conoce */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Nadie recomienda lo que no conoce</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                La clave del éxito en Greenway no es ser el mejor vendedor, es ser el <strong className="text-emerald-700">mejor 
                testimonio</strong>. Cuando hablas desde tu experiencia personal, tu mensaje es auténtico, creíble y poderoso.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Por eso, antes de vender, debes <strong>usar los productos</strong>. Siente la diferencia de limpiar sin químicos. 
                Observa cómo los paños Up Poly funcionan solo con agua. Experimenta el ahorro al usar productos concentrados. 
                Solo así podrás compartir tu verdad.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                Las personas no compran productos, compran <strong>resultados y experiencias</strong>. Cuando compartes tu historia 
                real de cómo estos productos mejoraron tu vida, estás ofreciendo algo que ningún anuncio puede igualar: confianza genuina.
              </p>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">💬</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Tu testimonio es tu herramienta más poderosa</h3>
                  <p className="text-gray-700">
                    "Yo usaba 5 productos químicos para limpiar mi casa. Ahora uso solo 2 paños Up Poly y agua. 
                    Ahorro dinero, tiempo y mi familia respira aire más limpio." - <em>Este tipo de mensaje vende más 
                    que cualquier folleto.</em>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Checklist */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Checklist: Productos Básicos</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Estos son los productos esenciales que recomendamos probar primero. Son los más versátiles, 
                los que usarás todos los días y los que te darán resultados inmediatos para compartir.
              </p>
            </div>

            <div className="space-y-4 mt-6">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-200">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">☐</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-emerald-900">Paño de Microfibra Up Poly Universal</h3>
                    <p className="text-gray-700">Para cocina, baño, muebles. Limpia todo solo con agua.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-200">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">☐</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-emerald-900">Paño para Vidrios</h3>
                    <p className="text-gray-700">Deja los vidrios impecables sin rayas ni químicos.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-200">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">☐</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-emerald-900">Fibra para Cuidado de la Piel</h3>
                    <p className="text-gray-700">Cuida tu piel de forma natural con tecnología Up Poly.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-200">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">☐</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-emerald-900">Trapeador y Balde</h3>
                    <p className="text-gray-700">Accesorios prácticos para una limpieza eficiente del hogar.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mt-6">
              <p className="text-lg text-blue-900 text-center">
                <strong>💡 Consejo:</strong> Mientras usas cada producto, toma nota mental de los beneficios 
                que observas. Esas serán las historias que compartirás con tus clientes.
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Filma tu experiencia */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Acción: Comparte tu Experiencia</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Ahora que has probado los productos y viviste los resultados, es momento de <strong className="text-emerald-700">compartir 
                tu testimonio</strong>. No necesitas ser un experto en video o redes sociales. Solo necesitas ser auténtico.
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-4 border-green-400 mt-6">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="text-4xl">💚</div>
                <h3 className="text-2xl font-bold text-green-900">ACCIÓN OBLIGATORIA</h3>
              </div>
              <p className="text-lg text-green-900 text-center mb-3">
                <strong>Comparte tu testimonio en el grupo de WhatsApp de AlianzaGreen</strong>
              </p>
              <p className="text-gray-700 text-center mb-4">
                Puede ser un video corto mostrando cómo usas el paño Up Poly, una foto antes/después, 
                o tu testimonio escrito. Compártelo en nuestro grupo oficial.
              </p>
              <div className="flex justify-center">
                <a
                  href="https://chat.whatsapp.com/K0pvTtqXWW69qpJ8DOHBwJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg rounded-lg font-medium inline-flex items-center gap-2"
                >
                  <span>💬</span>
                  Enviar mi Testimonio al Grupo
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200 mt-6">
              <h3 className="text-xl font-bold text-purple-900 mb-4">📱 Ideas para tu testimonio:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-3">1.</span>
                  <span><strong>Video corto (30-60 seg):</strong> Muestra cómo limpias con el paño Up Poly, sin químicos</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-3">2.</span>
                  <span><strong>Antes y después:</strong> Foto de una superficie sucia y luego limpia</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-3">3.</span>
                  <span><strong>Story en Instagram/Facebook:</strong> Cuenta qué producto probaste y qué te sorprendió</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-3">4.</span>
                  <span><strong>Envía por WhatsApp:</strong> Comparte tu experiencia directamente con tus contactos</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">✨ Fórmula simple para tu testimonio:</h3>
              <div className="space-y-2 text-gray-700">
                <p><strong>1. El problema:</strong> "Antes gastaba mucho en productos de limpieza..."</p>
                <p><strong>2. La solución:</strong> "Probé los paños Up Poly de Greenway..."</p>
                <p><strong>3. El resultado:</strong> "Ahora limpio todo con agua, ahorro y mi casa huele a limpio, no a químicos"</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Embajador del producto</p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 5: Primeros pasos en la comunicación
const Module5 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/5`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Comuniqué mi primera vez');
      navigate('/');
    } catch (error) {
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">📣 Módulo 5: Primeros pasos en la comunicación</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: Cómo hablar directo */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Cómo hablar directo</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Las fibras no hablan, <strong className="text-emerald-700">tú sí</strong>. Tu testimonio personal 
                es la herramienta más poderosa que tienes. No necesitas ser experto en ventas, solo necesitas ser 
                auténtico y compartir tu experiencia real.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Cuando hablas desde tu verdad, desde lo que viviste con los productos, tu mensaje es creíble y genera 
                confianza. No intentes convencer a nadie, simplemente comparte cómo los productos mejoraron tu vida.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mt-6">
              <h3 className="text-xl font-bold text-blue-900 mb-4">💬 Frases simples que funcionan:</h3>
              <div className="space-y-3 text-gray-700">
                <p>✓ "Antes gastaba mucho en productos de limpieza, ahora solo uso estos paños con agua"</p>
                <p>✓ "Lo mejor es que no hacen olor y duran más de 2 años"</p>
                <p>✓ "Mi casa está más limpia y sin químicos. Mi familia respira mejor"</p>
                <p>✓ "Probé este paño y me sorprendió. Te muestro cómo funciona"</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <p className="text-lg text-gray-800 text-center">
                <strong>💡 Regla de oro:</strong> Habla como hablarías con un amigo. Sin script, sin presión. 
                Solo comparte tu experiencia genuina.
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-4 border-purple-400 mt-6">
              <div className="text-center">
                <div className="text-4xl mb-3">📚</div>
                <h3 className="text-xl font-bold text-purple-900 mb-3">Capacitación: Chat Cliente</h3>
                <p className="text-gray-700 mb-4">
                  Aprende a gestionar conversaciones con clientes de forma profesional y efectiva
                </p>
                <Button
                  onClick={() => window.open('https://acrobat.adobe.com/id/urn:aaid:sc:VA6C2:a9d6aea1-8fb5-48ed-aada-bcfa3d12a33d', '_blank', 'noopener,noreferrer')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3"
                  size="lg"
                >
                  📖 Ver Capacitación
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Cómo usar redes */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Cómo usar las Redes Sociales</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Las redes sociales son tus aliadas para compartir tu experiencia con Greenway. No necesitas ser 
                influencer ni tener miles de seguidores. Con tus contactos actuales es suficiente para empezar.
              </p>
            </div>

            <div className="space-y-4 mt-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl">💬</div>
                  <div>
                    <h3 className="text-xl font-bold text-green-900 mb-2">WhatsApp</h3>
                    <p className="text-gray-700 mb-3">
                      Tu herramienta principal. Comparte videos cortos en estados o envía mensajes directos a 
                      personas que sabes que les puede interesar.
                    </p>
                    <p className="text-green-800 text-sm">
                      <strong>Tip:</strong> "Hola [nombre], probé esto y pensé en ti. Te muestro..."
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-xl border border-pink-200">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl">📸</div>
                  <div>
                    <h3 className="text-xl font-bold text-purple-900 mb-2">Instagram</h3>
                    <p className="text-gray-700 mb-3">
                      Stories y reels cortos mostrando el antes/después. Usa hashtags: #LimpiezaNatural 
                      #SinQuimicos #Greenway #EcoFriendly
                    </p>
                    <p className="text-purple-800 text-sm">
                      <strong>Tip:</strong> Videos de 15-30 segundos son perfectos
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl">📘</div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-900 mb-2">Facebook</h3>
                    <p className="text-gray-700 mb-3">
                      Publica en tu muro personal o en grupos de tu comunidad. Fotos con descripción 
                      de tu experiencia funcionan muy bien.
                    </p>
                    <p className="text-blue-800 text-sm">
                      <strong>Tip:</strong> Responde a los comentarios, genera conversación
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-xl border border-red-200">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl">🎵</div>
                  <div>
                    <h3 className="text-xl font-bold text-red-900 mb-2">TikTok</h3>
                    <p className="text-gray-700 mb-3">
                      Videos cortos y dinámicos. Muestra cómo limpias con el paño, sin edición compleja. 
                      Lo auténtico funciona mejor.
                    </p>
                    <p className="text-red-800 text-sm">
                      <strong>Tip:</strong> Usa música trending para más alcance
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-4 border-purple-400 mt-6">
              <div className="text-center">
                <div className="text-4xl mb-3">📚</div>
                <h3 className="text-xl font-bold text-purple-900 mb-3">Capacitación: Chat Cliente</h3>
                <p className="text-gray-700 mb-4">
                  Aprende a gestionar conversaciones con clientes de forma profesional y efectiva
                </p>
                <Button
                  onClick={() => window.open('https://acrobat.adobe.com/id/urn:aaid:sc:VA6C2:a9d6aea1-8fb5-48ed-aada-bcfa3d12a33d', '_blank', 'noopener,noreferrer')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3"
                  size="lg"
                >
                  📖 Ver Capacitación
                </Button>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Canal #GwArgentina */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Canal #GwArgentina en YouTube</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Tenemos un canal oficial en YouTube con contenido exclusivo del equipo. Aquí encontrarás videos 
                de productos, testimonios, capacitaciones y recursos que puedes compartir con tus contactos.
              </p>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-pink-50 p-8 rounded-xl border-4 border-red-400 mt-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">📺</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">#GwArgentina</h3>
                <p className="text-gray-700 text-lg">Canal Oficial de YouTube</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl mb-4">
                <h4 className="text-xl font-bold text-gray-900 mb-3">📹 Qué encontrarás:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Videos demostrativos de productos</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Testimonios de distribuidores exitosos</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Tutoriales de uso de los paños Up Poly</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Capacitaciones sobre el negocio</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Contenido para compartir con tus contactos</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mt-6">
              <p className="text-lg text-blue-900 text-center">
                <strong>💡 Úsalo como herramienta:</strong> En lugar de explicar todo tú, comparte un video 
                del canal. Es más fácil y profesional. Los videos ya están hechos para ti.
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-4 border-purple-400 mt-6">
              <div className="text-center">
                <div className="text-4xl mb-3">📚</div>
                <h3 className="text-xl font-bold text-purple-900 mb-3">Capacitación: Chat Cliente</h3>
                <p className="text-gray-700 mb-4">
                  Aprende a gestionar conversaciones con clientes de forma profesional y efectiva
                </p>
                <Button
                  onClick={() => window.open('https://acrobat.adobe.com/id/urn:aaid:sc:VA6C2:a9d6aea1-8fb5-48ed-aada-bcfa3d12a33d', '_blank', 'noopener,noreferrer')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3"
                  size="lg"
                >
                  📖 Ver Capacitación
                </Button>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Comuniqué mi primera vez</p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 6: Define tu Avatar
const Module6 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);
  const [avatarData, setAvatarData] = useState({
    target_audience: '',
    age_range: '',
    gender: '',
    interests: '',
    problems: '',
    goals: ''
  });

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/6`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Sé a quién le hablo');
      navigate('/');
    } catch (error) {
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">🎯 Módulo 6: Define tu Avatar</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: ¿A quién quiero hablarle? */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">¿A quién quiero hablarle?</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                No puedes hablarle a todo el mundo. Cuando intentas venderle a todos, terminas no vendiendo a nadie. 
                La clave del éxito es <strong className="text-emerald-700">enfocarte en un tipo de persona específica</strong>: 
                tu cliente ideal o "avatar".
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Tu avatar es la representación de tu cliente perfecto. Alguien que realmente necesita lo que ofreces, 
                que tiene el problema que tú puedes resolver, y que está buscando una solución como la tuya.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                Cuando defines bien tu avatar, tu mensaje se vuelve más claro, más directo y más efectivo. Hablas 
                específicamente a esa persona y ella se siente identificada inmediatamente.
              </p>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">❌ Mensaje genérico vs. ✅ Mensaje enfocado:</h3>
              <div className="space-y-3">
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-red-900">
                    <strong>❌ Genérico:</strong> "Tengo productos de limpieza para todos"
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-green-900">
                    <strong>✅ Enfocado:</strong> "¿Sos mamá y te preocupa que tus hijos respiren químicos? 
                    Te muestro cómo limpio sin tóxicos"
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mt-6">
              <p className="text-lg text-blue-900 text-center">
                <strong>💡 Recuerda:</strong> Tu avatar no limita tu mercado, lo enfoca. Después de hablarle 
                bien a uno, otros también te escucharán.
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Ejemplo de avatar ideal */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Ejemplo de Avatar Ideal</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Veamos un ejemplo concreto de cómo definir un avatar. Esto te ayudará a entender el nivel 
                de detalle que necesitas para tu propio cliente ideal.
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-xl border-4 border-purple-400 mt-6">
              <div className="text-center mb-4">
                <div className="text-6xl mb-2">👩</div>
                <h3 className="text-2xl font-bold text-purple-900">María, 35 años</h3>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-purple-900 mb-2">📍 ¿Quién es?</p>
                  <p>Mamá de 2 hijos pequeños, trabaja desde casa, vive en zona urbana</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-purple-900 mb-2">😟 Su problema:</p>
                  <p>Gasta mucho en productos de limpieza, le preocupan los químicos que respiran sus hijos, 
                  no tiene tiempo para limpiar profundo todos los días</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-purple-900 mb-2">🎯 Lo que busca:</p>
                  <p>Una forma de limpiar rápido, efectiva, segura para sus hijos y que le ahorre dinero</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-purple-900 mb-2">💚 Por qué le sirve Greenway:</p>
                  <p>Los paños Up Poly limpian solo con agua (seguro para niños), son rápidos de usar, 
                  duran años (ahorro) y no generan olores químicos en casa</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-purple-900 mb-2">📱 Dónde la encuentras:</p>
                  <p>Grupos de Facebook de mamás, Instagram con contenido de maternidad y hogar, 
                  WhatsApp de colegio de los hijos</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 mt-6">
              <p className="text-lg text-green-900">
                <strong>✨ Con este nivel de detalle:</strong> Sabes exactamente qué decirle, dónde encontrarla, 
                qué problemas resolver y qué beneficios destacar. Tu mensaje será directo y efectivo.
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Definir mi Avatar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Herramientas para definir avatar */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Herramientas para Crear tu Avatar</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Usa estas preguntas estratégicas para construir un avatar claro y específico. Responde con el mayor 
                detalle posible. <strong className="text-emerald-700">Mientras más sepas de tu cliente ideal, más 
                fácil será hablarle y venderle.</strong>
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mb-6">
              <h3 className="text-xl font-bold text-blue-900 mb-3">🎯 Herramienta #1: Perfil Demográfico</h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>• Edad:</strong> ¿Cuántos años tiene tu cliente ideal? (Ej: 30-45 años)</p>
                <p><strong>• Género:</strong> ¿Masculino, femenino o ambos?</p>
                <p><strong>• Ubicación:</strong> ¿Zona urbana, suburbios, rural?</p>
                <p><strong>• Ocupación:</strong> ¿Trabaja? ¿Desde casa? ¿Es ama de casa? ¿Emprendedor?</p>
                <p><strong>• Situación familiar:</strong> ¿Tiene hijos? ¿Pareja? ¿Vive solo/a?</p>
                <p><strong>• Nivel de ingresos:</strong> ¿Ingresos bajos, medios, altos?</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200 mb-6">
              <h3 className="text-xl font-bold text-purple-900 mb-3">🧠 Herramienta #2: Perfil Psicográfico</h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>• Valores:</strong> ¿Qué le importa? (Ej: familia, salud, ahorro, medio ambiente)</p>
                <p><strong>• Estilo de vida:</strong> ¿Activo? ¿Ocupado? ¿Organizado? ¿Desorganizado?</p>
                <p><strong>• Intereses:</strong> ¿Qué le gusta hacer? (Ej: cocinar, redes sociales, deportes)</p>
                <p><strong>• Personalidad:</strong> ¿Práctico? ¿Detallista? ¿Escéptico? ¿Entusiasta?</p>
                <p><strong>• Miedos:</strong> ¿A qué le teme? (Ej: enfermedades, gastar de más, no tener tiempo)</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 mb-6">
              <h3 className="text-xl font-bold text-green-900 mb-3">💭 Herramienta #3: Problemas y Deseos</h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>• Problema principal:</strong> ¿Cuál es su mayor frustración? (Ej: gasta mucho en limpieza)</p>
                <p><strong>• Dolor emocional:</strong> ¿Cómo se siente por ese problema? (Ej: preocupada, cansada)</p>
                <p><strong>• Objeciones:</strong> ¿Por qué no ha comprado antes? (Ej: cree que es caro, no confía)</p>
                <p><strong>• Deseo profundo:</strong> ¿Qué realmente quiere lograr? (Ej: hogar sano para sus hijos)</p>
                <p><strong>• Solución ideal:</strong> ¿Qué busca en un producto? (Ej: rápido, económico, seguro)</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-xl border border-orange-200 mb-6">
              <h3 className="text-xl font-bold text-orange-900 mb-3">📱 Herramienta #4: Hábitos de Consumo</h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>• Redes sociales:</strong> ¿Dónde pasa su tiempo? (Instagram, Facebook, TikTok, WhatsApp)</p>
                <p><strong>• Grupos y comunidades:</strong> ¿A qué grupos pertenece? (grupos de mamás, emprendedores)</p>
                <p><strong>• Cómo investiga:</strong> ¿Lee reseñas? ¿Ve videos? ¿Pregunta a conocidos?</p>
                <p><strong>• Cómo compra:</strong> ¿Impulsivo? ¿Analítico? ¿Necesita testimonios?</p>
                <p><strong>• Momento de compra:</strong> ¿Cuándo está más receptivo? (fin de semana, noche)</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-xl border border-yellow-200 mb-6">
              <h3 className="text-xl font-bold text-yellow-900 mb-3">✍️ Herramienta #5: Crea tu Frase Avatar</h3>
              <p className="text-gray-700 mb-3">
                Completa esta frase para tener claridad total sobre tu cliente ideal:
              </p>
              <div className="bg-white p-4 rounded-lg border border-yellow-300">
                <p className="text-gray-800 leading-relaxed">
                  <strong>"Mi cliente ideal es [nombre ficticio], tiene [edad] años, es [ocupación/rol]. 
                  Su mayor problema es [problema]. Lo que más desea es [deseo]. Le hablo en [red social] 
                  porque ahí pasa su tiempo. Le ofrezco [producto] que le ayuda a [beneficio específico]."</strong>
                </p>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                Ejemplo: "Mi cliente ideal es Ana, tiene 38 años, es mamá trabajadora. Su mayor problema es 
                que gasta mucho en limpieza y le preocupan los químicos. Lo que más desea es un hogar limpio 
                y seguro para sus hijos sin gastar tanto. Le hablo en grupos de WhatsApp de mamás porque ahí 
                comparte consejos. Le ofrezco paños Up Poly que le ayudan a limpiar solo con agua, ahorrar y 
                proteger la salud de su familia."
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-4 border-green-400 mt-6">
              <div className="text-center">
                <div className="text-4xl mb-3">💬</div>
                <h3 className="text-xl font-bold text-green-900 mb-3">Envía tu Avatar a tu Líder</h3>
                <p className="text-gray-700 mb-4">
                  Comparte con tu líder a quién le deseas hablar. Te ayudará a refinar tu estrategia 
                  y encontrar los mejores mensajes para tu avatar.
                </p>
                <a
                  href="https://chat.whatsapp.com/K0pvTtqXWW69qpJ8DOHBwJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg rounded-lg font-medium inline-flex items-center gap-2"
                >
                  <span>📲</span>
                  Enviar mi Avatar al Líder
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Sé a quién le hablo</p>
              <p className="text-emerald-700 text-sm mt-2">
                Guarda estas preguntas y úsalas para refinar tu avatar con el tiempo
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 7: Recursos del Equipo
const Module7 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/7`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Tengo mis herramientas');
      navigate('/');
    } catch (error) {
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">🛠️ Módulo 7: Recursos del Equipo</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: Canal de Telegram - Videos */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Canal de Telegram: Videos de Productos</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Tenemos un canal de Telegram exclusivo con <strong className="text-emerald-700">videos listos 
                para compartir</strong>. No necesitas crear contenido desde cero, solo descarga y comparte.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Estos videos ya están hechos profesionalmente, muestran los productos en acción y explican 
                sus beneficios de forma clara. Son perfectos para enviar por WhatsApp, publicar en redes o 
                mostrar en vivo a tus contactos.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-8 rounded-xl border-4 border-blue-400 mt-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">📹</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Videos de Productos</h3>
                <p className="text-gray-700 text-lg">Canal de Telegram</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">🎥 Qué encontrarás:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Videos demostrativos de cada producto</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Antes y después de limpieza con paños Up Poly</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Tutoriales de uso paso a paso</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Testimonios de clientes reales</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Videos cortos listos para compartir</span>
                  </li>
                </ul>
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={() => window.open('https://t.me/conociendoProductos', '_blank', 'noopener,noreferrer')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg"
                  size="lg"
                >
                  📹 Unirme al Canal de Videos
                </Button>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <p className="text-lg text-gray-800 text-center">
                <strong>💡 Tip:</strong> Descarga los videos que más te gusten y compártelos directamente 
                con tus contactos. Es más fácil que explicar todo tú.
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Canal de Telegram - Información General */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Canal de Telegram: Greenway Global Español</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Este es el canal oficial de <strong className="text-emerald-700">Greenway Global en español</strong>. 
                Aquí encontrarás toda la información actualizada sobre la empresa, productos, promociones y noticias importantes.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Mantente al día con los lanzamientos, actualizaciones del plan de compensación, eventos y todo lo que 
                necesitas saber para estar informado y actualizado en tu negocio.
              </p>
            </div>

            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-8 rounded-xl border-4 border-emerald-400 mt-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">📢</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Greenway Global Español</h3>
                <p className="text-gray-700 text-lg">Canal Oficial en Telegram</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">📰 Qué encontrarás:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2">•</span>
                    <span>Noticias oficiales de Greenway Global</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2">•</span>
                    <span>Lanzamientos de nuevos productos</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2">•</span>
                    <span>Promociones y ofertas especiales</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2">•</span>
                    <span>Actualizaciones del plan de compensación</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2">•</span>
                    <span>Eventos y capacitaciones en español</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-600 mr-2">•</span>
                    <span>Información importante para distribuidores</span>
                  </li>
                </ul>
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={() => window.open('https://t.me/greenwayglobalspanish', '_blank', 'noopener,noreferrer')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg"
                  size="lg"
                >
                  📢 Unirme al Canal Oficial
                </Button>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mt-6">
              <p className="text-lg text-blue-900 text-center">
                <strong>💡 Importante:</strong> Activa las notificaciones para no perderte ninguna actualización 
                importante del negocio.
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Grupo de WhatsApp */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Grupo de WhatsApp AlianzaGreen</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                El grupo de WhatsApp es tu <strong className="text-emerald-700">comunidad activa</strong> donde 
                puedes hacer preguntas, compartir experiencias, celebrar tus logros y recibir apoyo directo del equipo.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Aquí estamos todos juntos: distribuidores nuevos, veteranos y líderes. Nadie está solo en este camino. 
                Cuando tengas dudas, cuando logres una venta, cuando necesites motivación, este es tu espacio.
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-xl border-4 border-green-400 mt-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">💬</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Grupo AlianzaGreen</h3>
                <p className="text-gray-700 text-lg">WhatsApp - Soporte del Equipo</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">🤝 En este grupo:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    <span>Resuelves dudas en tiempo real</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    <span>Compartes tus testimonios y logros</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    <span>Recibes apoyo y motivación del equipo</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    <span>Aprendes de otros distribuidores</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    <span>Te enteras de eventos y reuniones</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    <span>Construyes relaciones con el equipo</span>
                  </li>
                </ul>
              </div>

              <div className="flex justify-center">
                <a
                  href="https://chat.whatsapp.com/K0pvTtqXWW69qpJ8DOHBwJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg rounded-lg font-medium inline-flex items-center gap-2"
                >
                  💬 Unirme al Grupo de WhatsApp
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200 mt-6">
              <p className="text-lg text-purple-900 text-center">
                <strong>❤️ Juntos somos más fuertes:</strong> No estás solo en este camino. El equipo 
                AlianzaGreen está aquí para ayudarte a crecer y alcanzar tus metas.
              </p>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-8 rounded-xl border-4 border-orange-400 mt-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">📅</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Agenda Semanal</h3>
              </div>
              
              {/* LUNES - Presentación de Negocio */}
              <div className="bg-white p-6 rounded-xl mb-4 border-l-4 border-blue-500">
                <h4 className="text-2xl font-bold text-blue-900 mb-2">🗓️ LUNES</h4>
                <p className="text-lg font-semibold text-gray-800 mb-4">Presentación de Negocio</p>
                <div className="grid md:grid-cols-2 gap-3 text-gray-700">
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                    <p><strong>🇦🇷 Argentina:</strong> 20:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                    <p><strong>🇨🇴 Colombia:</strong> 18:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                    <p><strong>🇪🇸 España:</strong> 10:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                    <p><strong>🇧🇴 Bolivia:</strong> 19:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                    <p><strong>🇨🇱 Chile:</strong> 20:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                    <p><strong>🇵🇾 Paraguay:</strong> 19:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                    <p><strong>🇵🇪 Perú:</strong> 18:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                    <p><strong>🇲🇽 México:</strong> 17:30 hs</p>
                  </div>
                </div>
              </div>

              {/* MARTES - Reunión de Equipo */}
              <div className="bg-white p-6 rounded-xl border-l-4 border-green-500">
                <h4 className="text-2xl font-bold text-green-900 mb-2">🗓️ MARTES</h4>
                <p className="text-lg font-semibold text-gray-800 mb-2">Reunión de Equipo</p>
                <p className="text-gray-700 mb-4">Preguntas y Respuestas • Estrategias del Período • Promociones</p>
                <div className="grid md:grid-cols-2 gap-3 text-gray-700">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                    <p><strong>🇦🇷 Argentina:</strong> 20:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                    <p><strong>🇨🇴 Colombia:</strong> 18:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                    <p><strong>🇪🇸 España:</strong> 10:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                    <p><strong>🇧🇴 Bolivia:</strong> 19:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                    <p><strong>🇨🇱 Chile:</strong> 20:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                    <p><strong>🇵🇾 Paraguay:</strong> 19:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                    <p><strong>🇵🇪 Perú:</strong> 18:30 hs</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                    <p><strong>🇲🇽 México:</strong> 17:30 hs</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-300 mt-4">
                <p className="text-center text-gray-800">
                  <strong>⏰ Importante:</strong> Estas reuniones son clave para tu éxito. 
                  Aprenderás estrategias, resolverás dudas y te motivarás con el equipo. ¡No faltes!
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Tengo mis herramientas</p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 8: Conceptos Clave del Negocio (PV, PGV, Rangos)
const Module8 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/8`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Domino los números');
      navigate('/');
    } catch (error) {
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">📊 Módulo 8: Conceptos Clave del Negocio</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 3</p>
        </div>

        {/* Screen 1: PV - Puntos de Volumen Personal */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Puntos de Volumen Personal (PV)</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Los <strong className="text-emerald-700">PV (Puntos de Volumen Personal)</strong> son la base de tu negocio en Greenway. 
                Representan el valor que generas con tu actividad comercial.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl border-4 border-blue-400 mt-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">📈</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">¿Qué son los PV?</h3>
              </div>
              
              <div className="bg-white p-6 rounded-xl mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">Los PV se generan de dos formas:</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border-2 border-emerald-300">
                    <div className="text-center mb-4">
                      <div className="text-4xl mb-2">🛒</div>
                      <h5 className="text-xl font-bold text-emerald-900">Tu Compra Personal</h5>
                    </div>
                    <p className="text-gray-700 text-center">
                      Cada producto que compras para ti o tu hogar genera PV a tu cuenta.
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-300">
                    <div className="text-center mb-4">
                      <div className="text-4xl mb-2">🔗</div>
                      <h5 className="text-xl font-bold text-blue-900">Ventas por tu Link</h5>
                    </div>
                    <p className="text-gray-700 text-center">
                      Cuando alguien compra a través de tu link personal, esos puntos también se suman a tu PV.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">💡</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Ejemplo práctico</h3>
                  <p className="text-gray-700">
                    Si compras productos por 100 PV y vendes a través de tu link productos por 50 PV, 
                    tu <strong>PV total del mes = 150 PV</strong>.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: PGV - Puntos de Grupo */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Puntos de Grupo (PGV)</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Los <strong className="text-emerald-700">PGV (Puntos de Grupo de Volumen)</strong> son la suma de 
                todos los puntos generados por tu equipo más tus puntos personales. Aquí es donde empieza el verdadero poder del negocio.
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-xl border-4 border-purple-400 mt-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">👥</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">¿Qué son los PGV?</h3>
              </div>
              
              <div className="bg-white p-6 rounded-xl mb-6">
                <div className="text-center mb-6">
                  <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-6 rounded-xl inline-block">
                    <p className="text-2xl font-bold text-purple-900">
                      PGV = Tus PV + PV de tu Equipo
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-center space-x-4 flex-wrap gap-2">
                    <div className="bg-emerald-100 p-4 rounded-xl text-center">
                      <p className="text-sm text-gray-600">Tus PV</p>
                      <p className="text-2xl font-bold text-emerald-700">150</p>
                    </div>
                    <div className="text-3xl font-bold text-gray-400">+</div>
                    <div className="bg-blue-100 p-4 rounded-xl text-center">
                      <p className="text-sm text-gray-600">PV de tu Equipo</p>
                      <p className="text-2xl font-bold text-blue-700">600</p>
                    </div>
                    <div className="text-3xl font-bold text-gray-400">=</div>
                    <div className="bg-purple-100 p-4 rounded-xl text-center">
                      <p className="text-sm text-gray-600">Tu PGV Total</p>
                      <p className="text-2xl font-bold text-purple-700">750</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200 mt-6">
              <h3 className="text-xl font-bold text-emerald-900 mb-4">🎯 ¿Por qué importan los PGV?</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span>Los PGV determinan tu <strong>rango</strong> en la empresa</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span>A mayor PGV, mayor <strong>porcentaje de bono grupal</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 text-xl mr-3">✓</span>
                  <span>Reflejan el <strong>crecimiento real</strong> de tu negocio</span>
                </li>
              </ul>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(1)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={() => setCurrentScreen(3)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Ver Sistema de Rangos <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Sistema de Rangos y Bonos */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Sistema de Rangos y Bonos Grupales</h2>
            
            <div className="prose prose-lg max-w-none space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                A medida que creces en PGV, avanzas en <strong className="text-emerald-700">rangos</strong> que te dan 
                acceso a mayores porcentajes de <strong className="text-emerald-700">bonos grupales</strong>.
              </p>
            </div>

            {/* Tabla de Rangos */}
            <div className="overflow-x-auto mt-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
                    <th className="p-4 text-left font-bold">🏆 Rango</th>
                    <th className="p-4 text-center font-bold">📊 PGV Requeridos</th>
                    <th className="p-4 text-center font-bold">💰 Bono Grupal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-blue-50 border-b border-blue-100">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">🥉</span>
                        <span className="font-bold text-blue-900">S1</span>
                      </div>
                    </td>
                    <td className="p-4 text-center font-semibold text-blue-800">750 PGV</td>
                    <td className="p-4 text-center">
                      <span className="bg-blue-200 text-blue-900 px-4 py-2 rounded-full font-bold text-lg">3%</span>
                    </td>
                  </tr>
                  <tr className="bg-indigo-50 border-b border-indigo-100">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">🥈</span>
                        <span className="font-bold text-indigo-900">S2</span>
                      </div>
                    </td>
                    <td className="p-4 text-center font-semibold text-indigo-800">1,500 PGV</td>
                    <td className="p-4 text-center">
                      <span className="bg-indigo-200 text-indigo-900 px-4 py-2 rounded-full font-bold text-lg">6%</span>
                    </td>
                  </tr>
                  <tr className="bg-emerald-50 border-b border-emerald-100">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">🥇</span>
                        <span className="font-bold text-emerald-900">Líder</span>
                      </div>
                    </td>
                    <td className="p-4 text-center font-semibold text-emerald-800">3,000 PGV</td>
                    <td className="p-4 text-center">
                      <span className="bg-emerald-200 text-emerald-900 px-4 py-2 rounded-full font-bold text-lg">12%</span>
                    </td>
                  </tr>
                  <tr className="bg-yellow-50 border-b border-yellow-100">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">⭐</span>
                        <span className="font-bold text-yellow-900">Pro</span>
                      </div>
                    </td>
                    <td className="p-4 text-center font-semibold text-yellow-800">5,000 PV + 5 Socios</td>
                    <td className="p-4 text-center">
                      <span className="bg-yellow-200 text-yellow-900 px-4 py-2 rounded-full font-bold text-lg">15%</span>
                    </td>
                  </tr>
                  <tr className="bg-purple-50">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">👑</span>
                        <span className="font-bold text-purple-900">Master</span>
                      </div>
                    </td>
                    <td className="p-4 text-center font-semibold text-purple-800">Generar Líderes</td>
                    <td className="p-4 text-center">
                      <span className="bg-purple-200 text-purple-900 px-4 py-2 rounded-full font-bold text-lg">+Bonos</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Detalles de Pro */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border-2 border-yellow-300 mt-6">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">⭐</div>
                <div>
                  <h3 className="text-xl font-bold text-yellow-900 mb-2">Rango PRO - Requisito Especial</h3>
                  <p className="text-gray-700">
                    Para alcanzar el rango <strong>Pro</strong> y pasar del 12% al <strong>15%</strong>, necesitas:
                  </p>
                  <ul className="mt-2 space-y-1 text-gray-700">
                    <li>• Generar <strong>5,000 PV</strong></li>
                    <li>• Tener al menos <strong>5 socios activos</strong> en tu primera línea</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Detalles de Master */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-300 mt-4">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">👑</div>
                <div>
                  <h3 className="text-xl font-bold text-purple-900 mb-2">Rango MASTER - El Siguiente Nivel</h3>
                  <p className="text-gray-700">
                    Para alcanzar el rango <strong>Master</strong> deberás <strong>generar Líderes en tus líneas</strong>. 
                    Este es el verdadero poder de la duplicación: cuando tu equipo crece, tú creces.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">Domino los números</p>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                variant="outline"
                className="border-emerald-200 text-emerald-700"
              >
                ← Atrás
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module 9: Plan de Marketing 4.0 (Video)
const Module9 = ({ user }) => {
  const navigate = useNavigate();

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/9`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: Ya entiendo el negocio');
      navigate('/');
    } catch (error) {
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">💼 Módulo 9: Plan de Marketing 4.0</h1>
          <p className="text-emerald-100">Entendiendo cómo se gana dinero en Greenway</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Video: Plan de Compensación</h2>
          
          <div className="prose prose-lg max-w-none space-y-4">
            <p className="text-lg text-gray-700 leading-relaxed">
              Este video te explica en detalle el <strong className="text-emerald-700">Plan de Marketing 4.0</strong> de Greenway Global. 
              Aprenderás cómo funciona el sistema de compensación y cómo puedes generar ingresos.
            </p>
          </div>

          {/* Video Section */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 rounded-xl border-4 border-red-400 mt-6">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🎬</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Plan de Marketing 4.0</h3>
              <p className="text-gray-700 text-lg">Video explicativo completo</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl mb-6 text-center">
              <p className="text-lg text-gray-700 mb-4">
                Haz clic en el botón para ver el video completo del Plan de Marketing 4.0
              </p>
            </div>

            <div className="flex justify-center">
              <a
                href="https://youtu.be/E6HkyYAghpc?si=8_AbLFl9lUvYeM1M"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg rounded-lg font-medium inline-flex items-center gap-2 transition-colors"
              >
                🎬 Ver Video en YouTube
              </a>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200 mt-6">
            <h3 className="text-xl font-bold text-emerald-900 mb-4">📌 Lo que aprenderás en este video:</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-emerald-600 text-xl mr-3">✓</span>
                <span>Diferencia entre vendedor y armador de red</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 text-xl mr-3">✓</span>
                <span>Cómo funciona la ganancia del 35% en billetera</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 text-xl mr-3">✓</span>
                <span>El poder de la duplicación</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 text-xl mr-3">✓</span>
                <span>Venta directa y por link en +50 países</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
            <p className="text-lg text-gray-800 text-center">
              <strong>⚠️ Importante:</strong> Mira el video completo antes de continuar. 
              Es fundamental que entiendas el plan de compensación para tu éxito en Greenway.
            </p>
          </div>

          <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
            <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
            <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
            <p className="text-emerald-900 text-xl font-bold">Ya entiendo el negocio</p>
          </div>

          <div className="flex justify-between pt-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-emerald-200 text-emerald-700"
            >
              ← Volver al Dashboard
            </Button>
            <Button
              onClick={handleComplete}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            >
              Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Module 10: Estrategias para Acelerar Resultados
const Module10 = ({ user }) => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);

  const handleComplete = async () => {
    if (!user || !user.id) {
      toast.error('Error: No se encontró el usuario.');
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/complete-module/10`);
      toast.success('¡Módulo completado! 🏅 Medalla desbloqueada: ¡Activo en mi camino Greenway!');
      navigate('/');
    } catch (error) {
      toast.error('Error al completar módulo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg mb-8">
          <h1 className="text-2xl font-bold mb-2">🚀 Módulo 10: Estrategias para Acelerar Resultados</h1>
          <p className="text-emerald-100">Pantalla {currentScreen} de 6</p>
        </div>

        {/* Screen 1: Estrategias de Prospección */}
        {currentScreen === 1 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">🎯 Estrategias de Prospección</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              La prospección es el <strong className="text-emerald-700">motor de tu negocio</strong>. 
              Sin prospectos, no hay ventas ni equipo. Aquí te enseñamos cómo encontrar personas interesadas.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-200">
                <div className="text-3xl mb-3">📱</div>
                <h3 className="text-xl font-bold text-blue-900 mb-2">Lista de Contactos</h3>
                <p className="text-gray-700">Empieza con tu lista de contactos del celular. Todos conocemos al menos 200 personas.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200">
                <div className="text-3xl mb-3">🌐</div>
                <h3 className="text-xl font-bold text-purple-900 mb-2">Redes Sociales</h3>
                <p className="text-gray-700">Instagram, Facebook, TikTok. Publica contenido de valor y atrae personas interesadas.</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200">
                <div className="text-3xl mb-3">👥</div>
                <h3 className="text-xl font-bold text-green-900 mb-2">Referidos</h3>
                <p className="text-gray-700">Pide a tus clientes satisfechos que te recomienden. ¡El boca a boca es poderoso!</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-xl border-2 border-orange-200">
                <div className="text-3xl mb-3">🎪</div>
                <h3 className="text-xl font-bold text-orange-900 mb-2">Eventos</h3>
                <p className="text-gray-700">Ferias, reuniones, eventos sociales. Lleva siempre tu catálogo o muestras.</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200 mt-6">
              <p className="text-lg text-gray-800">
                <strong>💡 Tip:</strong> Habla con al menos <strong>5 personas nuevas cada día</strong>. 
                En un mes serán 150 contactos potenciales.
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setCurrentScreen(2)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 2: Seguimiento Efectivo */}
        {currentScreen === 2 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">📞 Seguimiento Efectivo</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              El <strong className="text-emerald-700">80% de las ventas</strong> se cierran después del 5to contacto. 
              La mayoría abandona en el primero. ¡El seguimiento es la clave!
            </p>

            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border-2 border-emerald-300 mt-6">
              <h3 className="text-xl font-bold text-emerald-900 mb-4">📅 Regla del Seguimiento</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-4 bg-white p-4 rounded-lg">
                  <span className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">1</span>
                  <span className="text-gray-700"><strong>Día 1:</strong> Primer contacto - Presenta el producto/oportunidad</span>
                </div>
                <div className="flex items-center space-x-4 bg-white p-4 rounded-lg">
                  <span className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">2</span>
                  <span className="text-gray-700"><strong>Día 3:</strong> Seguimiento - "¿Pudiste ver la info que te envié?"</span>
                </div>
                <div className="flex items-center space-x-4 bg-white p-4 rounded-lg">
                  <span className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">3</span>
                  <span className="text-gray-700"><strong>Día 7:</strong> Llamada - Resuelve dudas y ofrece una demo</span>
                </div>
                <div className="flex items-center space-x-4 bg-white p-4 rounded-lg">
                  <span className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">4</span>
                  <span className="text-gray-700"><strong>Día 14:</strong> Cierre - "¿Estás listo/a para empezar?"</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-xl border border-red-200 mt-6">
              <p className="text-lg text-gray-800">
                <strong>⚠️ Error común:</strong> No hacer seguimiento por miedo a "molestar". 
                Recuerda: estás <strong>ofreciendo una solución</strong>, no molestando.
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button onClick={() => setCurrentScreen(1)} variant="outline" className="border-emerald-200 text-emerald-700">
                ← Atrás
              </Button>
              <Button onClick={() => setCurrentScreen(3)} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 3: Uso de Redes Sociales */}
        {currentScreen === 3 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">📲 Uso de Redes Sociales</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              Las redes sociales son tu <strong className="text-emerald-700">vitrina 24/7</strong>. 
              Úsalas estratégicamente para atraer clientes y socios.
            </p>

            <div className="space-y-4 mt-6">
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-6 rounded-xl border-2 border-pink-300">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-3xl">📸</span>
                  <h3 className="text-xl font-bold text-pink-900">Instagram</h3>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li>• Publica historias usando los productos</li>
                  <li>• Comparte testimonios de clientes</li>
                  <li>• Usa hashtags relevantes (#bienestar #negociodesdelacasa)</li>
                  <li>• Responde todos los mensajes rápidamente</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-300">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-3xl">💬</span>
                  <h3 className="text-xl font-bold text-green-900">WhatsApp</h3>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li>• Estados diarios mostrando tu estilo de vida</li>
                  <li>• Listas de difusión para promociones</li>
                  <li>• Catálogo de WhatsApp Business configurado</li>
                  <li>• Respuestas rápidas predeterminadas</li>
                </ul>
                <div className="mt-4 p-3 bg-green-100 rounded-lg">
                  <p className="text-green-800 text-sm">
                    <strong>📹 Tutorial:</strong> Mira cómo hacer un chat de atención al cliente en WhatsApp Business
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-300">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-3xl">👥</span>
                  <h3 className="text-xl font-bold text-blue-900">Facebook</h3>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li>• Grupos de comunidad (mamás, emprendedores)</li>
                  <li>• Lives mostrando productos</li>
                  <li>• Marketplace para ventas directas</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-6 rounded-xl border-2 border-gray-600">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-3xl">🎵</span>
                  <h3 className="text-xl font-bold text-white">TikTok</h3>
                </div>
                <ul className="space-y-2 text-gray-300">
                  <li>• Videos cortos mostrando resultados de productos</li>
                  <li>• Antes y después con música trending</li>
                  <li>• Tips rápidos de limpieza o bienestar</li>
                  <li>• Responde comentarios y haz duetos</li>
                </ul>
              </div>
            </div>

            {/* Enlaces importantes */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border-2 border-purple-300 mt-6">
              <h3 className="text-xl font-bold text-purple-900 mb-4 text-center">🔗 Recursos del Equipo AlianzaGreen</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <a
                  href="https://t.me/+IFkpc3UXtt85MTI5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-500 hover:bg-blue-600 text-white p-4 rounded-xl text-center font-medium transition-colors flex items-center justify-center gap-2"
                >
                  📢 Canal Telegram AlianzaGreenway
                </a>
                <a
                  href="https://www.youtube.com/@GWAYARGENTINA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-red-600 hover:bg-red-700 text-white p-4 rounded-xl text-center font-medium transition-colors flex items-center justify-center gap-2"
                >
                  🎬 Capacitaciones YouTube
                </a>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button onClick={() => setCurrentScreen(2)} variant="outline" className="border-emerald-200 text-emerald-700">
                ← Atrás
              </Button>
              <Button onClick={() => setCurrentScreen(4)} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 4: Duplicación */}
        {currentScreen === 4 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">👥 Duplicación: Enseña a tu Equipo</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              La <strong className="text-emerald-700">duplicación</strong> es el secreto del network marketing. 
              No se trata de lo que TÚ puedes hacer, sino de lo que tu equipo puede REPLICAR.
            </p>

            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-8 rounded-xl border-4 border-purple-300 mt-6">
              <div className="text-center mb-6">
                <div className="text-5xl mb-3">🔄</div>
                <h3 className="text-2xl font-bold text-purple-900">Principio de Duplicación</h3>
              </div>
              <p className="text-lg text-center text-gray-700 mb-4">
                "Si tú haces algo que otros no pueden copiar, <strong>no se duplica</strong>."
              </p>
              <p className="text-lg text-center text-gray-700">
                Mantén tu sistema <strong>SIMPLE</strong> para que cualquiera pueda hacerlo.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                <h4 className="text-lg font-bold text-green-800 mb-3">✅ Esto SÍ se duplica</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Compartir el catálogo</li>
                  <li>• Enviar un video de presentación</li>
                  <li>• Invitar a una reunión de Zoom</li>
                  <li>• Usar el mismo guión</li>
                </ul>
              </div>
              <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                <h4 className="text-lg font-bold text-red-800 mb-3">❌ Esto NO se duplica</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Hacer presentaciones de 2 horas</li>
                  <li>• Necesitar ser "experto" en todo</li>
                  <li>• Depender de tu carisma personal</li>
                  <li>• Procesos complicados</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button onClick={() => setCurrentScreen(3)} variant="outline" className="border-emerald-200 text-emerald-700">
                ← Atrás
              </Button>
              <Button onClick={() => setCurrentScreen(5)} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 5: Metas Semanales/Mensuales */}
        {currentScreen === 5 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">📊 Metas Semanales y Mensuales</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              Sin metas claras, no hay dirección. Define tus <strong className="text-emerald-700">números mínimos</strong> 
              y trabaja consistentemente para alcanzarlos.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-300 mt-6">
              <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">📅 Meta Semanal Sugerida</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-600">20</p>
                  <p className="text-sm text-gray-600">Contactos nuevos</p>
                </div>
                <div className="bg-white p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">5</p>
                  <p className="text-sm text-gray-600">Presentaciones</p>
                </div>
                <div className="bg-white p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-purple-600">2</p>
                  <p className="text-sm text-gray-600">Cierres</p>
                </div>
                <div className="bg-white p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-orange-600">1</p>
                  <p className="text-sm text-gray-600">Nuevo socio</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border-2 border-emerald-300 mt-6">
              <h3 className="text-xl font-bold text-emerald-900 mb-4 text-center">📆 Meta Mensual = Rango</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center bg-white p-4 rounded-lg">
                  <span className="font-semibold">Para llegar a S1 (750 PGV)</span>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">4-5 clientes activos</span>
                </div>
                <div className="flex justify-between items-center bg-white p-4 rounded-lg">
                  <span className="font-semibold">Para llegar a S2 (1500 PGV)</span>
                  <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full">8-10 clientes activos</span>
                </div>
                <div className="flex justify-between items-center bg-white p-4 rounded-lg">
                  <span className="font-semibold">Para llegar a Líder (3000 PGV)</span>
                  <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">Equipo de 3-5 socios</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button onClick={() => setCurrentScreen(4)} variant="outline" className="border-emerald-200 text-emerald-700">
                ← Atrás
              </Button>
              <Button onClick={() => setCurrentScreen(6)} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
                Continuar <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Screen 6: Tips de Cierre de Ventas */}
        {currentScreen === 6 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">💰 Tips de Cierre de Ventas</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              El cierre es simplemente <strong className="text-emerald-700">ayudar a la persona a tomar una decisión</strong>. 
              No es presionar, es guiar.
            </p>

            <div className="space-y-4 mt-6">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-xl border-2 border-yellow-300">
                <h3 className="text-xl font-bold text-yellow-900 mb-3">🎯 Técnica 1: La Pregunta Directa</h3>
                <p className="text-gray-700 italic">"¿Te gustaría empezar con el kit básico o prefieres el completo?"</p>
                <p className="text-sm text-gray-600 mt-2">Asume que ya decidió, solo elige la opción.</p>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-300">
                <h3 className="text-xl font-bold text-green-900 mb-3">🎯 Técnica 2: Resolver la Objeción</h3>
                <p className="text-gray-700 italic">"Entiendo que ahora no tienes el dinero completo. ¿Qué tal si empezamos con el paquete más pequeño?"</p>
                <p className="text-sm text-gray-600 mt-2">Ofrece alternativas, no te rindas al primer "no".</p>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-300">
                <h3 className="text-xl font-bold text-blue-900 mb-3">🎯 Técnica 3: Urgencia Real</h3>
                <p className="text-gray-700 italic">"Este mes tenemos una promoción especial que termina el viernes. ¿Lo aprovechamos?"</p>
                <p className="text-sm text-gray-600 mt-2">Crea urgencia genuina, no inventada.</p>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-300">
                <h3 className="text-xl font-bold text-purple-900 mb-3">🎯 Técnica 4: El Silencio</h3>
                <p className="text-gray-700 italic">Después de hacer tu oferta, quédate en silencio. Deja que la otra persona hable primero.</p>
                <p className="text-sm text-gray-600 mt-2">El que habla primero después de la oferta, pierde.</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-6 rounded-xl text-center mt-6">
              <Trophy className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-semibold">🏅 Medalla Desbloqueada:</p>
              <p className="text-emerald-900 text-xl font-bold">¡Activo en mi camino Greenway!</p>
            </div>

            <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-6 rounded-xl text-center mt-4">
              <p className="text-2xl font-bold text-orange-800">🎉 ¡FELICIDADES!</p>
              <p className="text-lg text-gray-700 mt-2">Has completado todos los módulos del programa de entrenamiento AlianzaGreen.</p>
              <p className="text-gray-600 mt-2">Ahora tienes todas las herramientas para triunfar. ¡El éxito depende de ti!</p>
            </div>

            <div className="flex justify-between pt-4">
              <Button onClick={() => setCurrentScreen(5)} variant="outline" className="border-emerald-200 text-emerald-700">
                ← Atrás
              </Button>
              <Button onClick={handleComplete} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
                Completar Módulo <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Module Screen Router
const ModuleScreen = ({ user }) => {
  const { moduleId } = useParams();
  const moduleIdNum = parseInt(moduleId);

  // Render specific module component based on ID
  if (moduleIdNum === 0) return <Module0 user={user} />;
  if (moduleIdNum === 1) return <Module1 user={user} />;
  if (moduleIdNum === 2) return <Module2 user={user} />;
  if (moduleIdNum === 3) return <Module3 user={user} />;
  if (moduleIdNum === 4) return <Module4 user={user} />;
  if (moduleIdNum === 5) return <Module5 user={user} />;
  if (moduleIdNum === 6) return <Module6 user={user} />;
  if (moduleIdNum === 7) return <Module7 user={user} />;
  if (moduleIdNum === 8) return <Module8 user={user} />;
  if (moduleIdNum === 9) return <Module9 user={user} />;
  if (moduleIdNum === 10) return <Module10 user={user} />;
  
  // Default for modules not yet implemented
  return <DefaultModuleScreen moduleId={moduleId} user={user} />;
};

// Default Module Screen (for modules 1-10 that we'll implement next)
const DefaultModuleScreen = ({ moduleId, user }) => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <AlianzGreenAssistantButton />
      <CatalogButton />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center space-y-8">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-8 rounded-2xl shadow-lg">
            <h1 className="text-2xl font-bold mb-2">🎓 Módulo {moduleId}</h1>
            <p className="text-emerald-100">Contenido en desarrollo</p>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-emerald-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">¡Excelente!</h2>
            <p className="text-lg text-gray-700 mb-6">
              Has llegado al Módulo {moduleId}. El contenido específico se está finalizando.
            </p>
            
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                ← Volver al Dashboard
              </Button>
              
              <Button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/users/${user.id}/complete-module/${moduleId}`);
                    toast.success(`¡Módulo ${moduleId} completado!`);
                    navigate('/');
                  } catch (error) {
                    toast.error('Error al completar módulo');
                  }
                }}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
              >
                Marcar como Completado
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Training App Content
const TrainingAppContent = ({ user }) => {
  const [modules, setModules] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    loadData();
  }, [user, location.pathname]);

  const loadData = async () => {
    try {
      const [modulesResponse, profileResponse] = await Promise.all([
        axios.get(`${API}/modules`),
        axios.get(`${API}/users/${user.id}/profile`)
      ]);
      
      setModules(modulesResponse.data);
      setUserProfile(profileResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse mx-auto"></div>
          <p className="text-gray-600">Cargando tu plataforma de AlianzaGreen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <Routes>
        <Route path="/" element={<ModuleDashboard modules={modules} userProfile={userProfile} user={user} />} />
        <Route path="/socios" element={<ModuleDashboard modules={modules} userProfile={userProfile} user={user} />} />
        <Route path="/module/:moduleId" element={<ModuleScreen user={user} />} />
      </Routes>
    </div>
  );
};

// Main Training App Component
const TrainingApp = ({ user }) => {
  return (
    <BrowserRouter>
      <TrainingAppContent user={user} />
    </BrowserRouter>
  );
};

// Prospecting Landing Page Component
const ProspectingLanding = ({ onGoToApp }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    whatsapp: '',
    email: '',
    ciudad: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Save the lead in the database so no contact is ever lost
    try {
      await axios.post(`${API}/leads`, {
        nombre: formData.nombre,
        whatsapp: formData.whatsapp,
        email: formData.email,
        ciudad: formData.ciudad
      });
    } catch (error) {
      // Even if saving fails, continue to WhatsApp so the lead reaches the team
      console.error('Error guardando lead:', error);
    }

    // Create WhatsApp message
    const message = `🌟 *NUEVO PROSPECTO ALIANZ GREEN*%0A%0A` +
      `👤 *Nombre:* ${formData.nombre}%0A` +
      `📱 *WhatsApp:* ${formData.whatsapp}%0A` +
      `📧 *Email:* ${formData.email}%0A` +
      `📍 *Ciudad:* ${formData.ciudad}%0A%0A` +
      `_Enviado desde Landing de Prospección_`;
    
    // Open WhatsApp with the message (same tab for better compatibility)
    window.location.href = `https://wa.me/5491172750655?text=${message}`;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">¡Gracias por tu interés!</h2>
          <p className="text-gray-600 mb-6">
            Tus datos fueron enviados. Nos pondremos en contacto contigo muy pronto para contarte más sobre esta increíble oportunidad.
          </p>
          <div className="bg-gradient-to-r from-emerald-100 to-teal-100 p-4 rounded-xl">
            <p className="text-emerald-800 font-medium">
              💬 Si no se abrió WhatsApp, escríbenos directamente al +54 9 11 7275-0655
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header with "Soy Socio" button */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-3xl">🌿</span>
            <span className="font-bold text-2xl">AlianzaGreen</span>
          </div>
          <button
            onClick={onGoToApp}
            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
          >
            🤝 Soy Socio
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">🌍 ¿Sabías que tu hogar está lleno de químicos tóxicos?</h1>
          <p className="text-emerald-100 text-lg">Descubre cómo proteger a tu familia y generar ingresos al mismo tiempo</p>
        </div>
      </div>

      {/* Mission Section - HIGHLIGHTED */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/30">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">🎯 NUESTRA MISIÓN</h2>
            <p className="text-xl md:text-2xl text-white font-semibold">
              Reducir plásticos y químicos del uso cotidiano
            </p>
            <p className="text-emerald-100 mt-2">Cuidamos tu hogar, tu salud y el planeta 🌱</p>
          </div>
        </div>
      </div>

      {/* My Video Section */}
      <div className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">👋 ¡Hola! Te cuento de qué se trata</h2>
          </div>
          <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-2xl bg-black max-w-2xl mx-auto">
            <video 
              controls 
              className="w-full h-full"
            >
              <source src="https://customer-assets.emergentagent.com/job_alianz-training-1/artifacts/dn6xdd2o_yo%20para%20landing.mp4" type="video/mp4" />
              Tu navegador no soporta videos HTML5.
            </video>
          </div>
        </div>
      </div>

      {/* Quick Access Buttons - 4 Options */}
      <div className="bg-gradient-to-br from-gray-50 to-emerald-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">📋 ¿Qué te gustaría saber?</h2>
            <p className="text-gray-600">Hacé click en lo que más te interesa</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* 1. Products */}
            <a
              href="#productos"
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center border-2 border-emerald-100 hover:border-emerald-400"
            >
              <div className="text-4xl mb-3">🧴</div>
              <h3 className="font-bold text-gray-900">Productos</h3>
              <p className="text-sm text-gray-600 mt-1">5 características</p>
            </a>
            
            {/* 2. Catalog */}
            <a
              href="https://customer-assets.emergentagent.com/job_greenway-process/artifacts/ukt3sehv_Greenway_catalog_ARG_05.09.2025_compressed.pdf.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center border-2 border-blue-100 hover:border-blue-400"
            >
              <div className="text-4xl mb-3">📖</div>
              <h3 className="font-bold text-gray-900">Catálogo</h3>
              <p className="text-sm text-gray-600 mt-1">Ver productos</p>
            </a>
            
            {/* 3. Earnings */}
            <a
              href="#ganancias"
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center border-2 border-yellow-100 hover:border-yellow-400"
            >
              <div className="text-4xl mb-3">💰</div>
              <h3 className="font-bold text-gray-900">Ganancias</h3>
              <p className="text-sm text-gray-600 mt-1">35% al 100%</p>
            </a>
            
            {/* 4. Price List */}
            <a
              href="https://customer-assets-jt897jd0.emergentagent.net/job_fa573c8b-cfbd-48ca-ae1d-4768ba6f6487/artifacts/ohmbuw3v_lista-precios-septiembre-2026.xlsx"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center border-2 border-purple-100 hover:border-purple-400"
            >
              <div className="text-4xl mb-3">📊</div>
              <h3 className="font-bold text-gray-900">Precios</h3>
              <p className="text-sm text-gray-600 mt-1">Lista Septiembre</p>
            </a>
          </div>
        </div>
      </div>

      {/* Products Section with 5 Characteristics */}
      <div id="productos" className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">🧴 Nuestros Productos</h2>
            <p className="text-gray-600">Tecnología alemana de alta calidad</p>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-center text-emerald-800 mb-6">✨ 5 Características que los hacen ÚNICOS</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">1</div>
                <div>
                  <h4 className="font-bold text-gray-900">🔬 Nanotecnológicos</h4>
                  <p className="text-sm text-gray-600">Tecnología de punta en cada fibra</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-xl">2</div>
                <div>
                  <h4 className="font-bold text-gray-900">💧 Solo con Agua</h4>
                  <p className="text-sm text-gray-600">No necesitan químicos ni detergentes</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl">3</div>
                <div>
                  <h4 className="font-bold text-gray-900">⏳ Duran +2 Años</h4>
                  <p className="text-sm text-gray-600">Alta durabilidad garantizada</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">4</div>
                <div>
                  <h4 className="font-bold text-gray-900">🦠 Antibacterias</h4>
                  <p className="text-sm text-gray-600">Eliminan bacterias de forma natural</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm md:col-span-2 md:max-w-md md:mx-auto">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl">5</div>
                <div>
                  <h4 className="font-bold text-gray-900">✨ Sin Olores ni Babosidades</h4>
                  <p className="text-sm text-gray-600">Siempre frescos y limpios</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-emerald-700 font-semibold">💪 Alta absorción y resistencia</p>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings Section */}
      <div id="ganancias" className="bg-gradient-to-br from-yellow-50 to-orange-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">💰 ¿Cuánto se Gana?</h2>
            <p className="text-gray-600">Tu esfuerzo determina tus ingresos</p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="inline-block bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-2xl">
                <p className="text-lg">Comisiones desde</p>
                <p className="text-4xl md:text-5xl font-bold">35% al 100%</p>
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-center text-gray-800 mb-6">📈 3 Formas de Generar Ingresos</h3>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-xl text-center border-2 border-emerald-200">
                <div className="text-4xl mb-3">🛍️</div>
                <h4 className="font-bold text-gray-900 text-lg">Venta Directa</h4>
                <p className="text-sm text-gray-600 mt-2">Vendé productos a tus clientes y ganá comisiones por cada venta</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl text-center border-2 border-blue-200">
                <div className="text-4xl mb-3">💻</div>
                <h4 className="font-bold text-gray-900 text-lg">E-commerce</h4>
                <p className="text-sm text-gray-600 mt-2">Generá ingresos con tu tienda online personal sin stock</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl text-center border-2 border-purple-200">
                <div className="text-4xl mb-3">👥</div>
                <h4 className="font-bold text-gray-900 text-lg">Armado de Equipos</h4>
                <p className="text-sm text-gray-600 mt-2">Formá tu red y ganá por las ventas de tu equipo</p>
              </div>
            </div>
            
            <p className="text-center text-gray-600 mt-6">
              🚀 Sin límite de ganancias • 🏠 Trabajá desde casa • ⏰ Horarios flexibles
            </p>
          </div>
        </div>
      </div>

      {/* Rewards Section - Cars & Trips */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">🏆 ¡Ganá Premios Increíbles!</h2>
            <p className="text-gray-300">Greenway premia tu esfuerzo con autos y viajes</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Cars - Video real del auto ganado */}
            <div className="rounded-2xl overflow-hidden shadow-2xl bg-white/10 backdrop-blur-sm">
              <video
                className="w-full h-64 object-cover"
                controls
                preload="metadata"
                data-testid="car-prize-video"
              >
                <source src="https://customer-assets-jt897jd0.emergentagent.net/job_fa573c8b-cfbd-48ca-ae1d-4768ba6f6487/artifacts/4n1zy1m1_WhatsApp%20Video%202026-08-23%20at%2013.11.00.mp4" type="video/mp4" />
                Tu navegador no soporta videos.
              </video>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-1">🚗 Programa de Autos</h3>
                <p className="text-gray-200">¡Auto ganado por nuestro equipo! Alcanzá tus metas y manejá el tuyo</p>
              </div>
            </div>
            
            {/* Trips */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img 
                src="https://customer-assets.emergentagent.com/job_alianz-training-1/artifacts/xgvd1jsm_image.png" 
                alt="Viajes de Incentivo Greenway"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-bold text-white mb-1">✈️ Viajes de Incentivo</h3>
                <p className="text-gray-200">Viajá por el mundo con tu equipo Greenway</p>
              </div>
            </div>
          </div>
          
          <p className="text-center text-gray-400 mt-6 text-sm">
            ✨ Greenway premia a sus líderes con los mejores incentivos del mercado
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Column - Video & Benefits */}
          <div className="space-y-8">
            {/* Video */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">🎬 Mira esto antes de continuar</h2>
              <div className="aspect-video w-full rounded-xl overflow-hidden shadow-lg bg-black">
                <video 
                  controls 
                  className="w-full h-full"
                  poster=""
                >
                  <source src="https://customer-assets.emergentagent.com/job_alianz-training/artifacts/pgdfq9cp_video%20de%20contaminacion%20evento.mp4" type="video/mp4" />
                  Tu navegador no soporta videos HTML5.
                </video>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">✨ ¿Qué te ofrece Greenway?</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl">
                  <div className="text-3xl">🏠</div>
                  <div>
                    <h3 className="font-bold text-gray-900">Productos Ecológicos</h3>
                    <p className="text-gray-600">Limpia tu hogar sin químicos tóxicos. Cuida la salud de tu familia.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                  <div className="text-3xl">💰</div>
                  <div>
                    <h3 className="font-bold text-gray-900">Oportunidad de Negocio</h3>
                    <p className="text-gray-600">Genera ingresos desde casa, con horarios flexibles y sin jefes.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                  <div className="text-3xl">👥</div>
                  <div>
                    <h3 className="font-bold text-gray-900">Comunidad de Apoyo</h3>
                    <p className="text-gray-600">Únete a AlianzaGreen, un equipo que te acompaña paso a paso.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl">
                  <div className="text-3xl">🌍</div>
                  <div>
                    <h3 className="font-bold text-gray-900">Presencia Global</h3>
                    <p className="text-gray-600">Greenway está en más de 60 países. Un negocio sin fronteras.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white">
              <div className="text-center">
                <div className="text-4xl mb-3">💬</div>
                <p className="text-lg italic mb-4">
                  "Empecé sin saber nada de ventas. Hoy tengo mi propio equipo y genero ingresos que nunca imaginé, 
                  todo desde mi casa mientras cuido a mis hijos."
                </p>
                <p className="font-bold">- Distribuidora de AlianzaGreen</p>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:sticky lg:top-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl border-4 border-emerald-200">
              <div className="text-center mb-6">
                <div className="text-5xl mb-3">🚀</div>
                <h2 className="text-2xl font-bold text-gray-900">¡Quiero saber más!</h2>
                <p className="text-gray-600">Completa tus datos y te contactamos</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
                  <input
                    type="text"
                    required
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Tu nombre"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp *</label>
                  <input
                    type="tel"
                    required
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="+54 9 11 1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad / País *</label>
                  <input
                    type="text"
                    required
                    value={formData.ciudad}
                    onChange={(e) => setFormData({...formData, ciudad: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ej: Buenos Aires, Argentina"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-4 px-6 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  📲 ¡Quiero que me contacten!
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  🔒 Tus datos están seguros. No compartimos tu información.
                </p>
              </div>

              {/* Social Links */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-center text-sm font-medium text-gray-700 mb-3">📱 ¡Únete a nuestra comunidad!</p>
                <div className="flex gap-3">
                  <a
                    href="https://t.me/conociendoProductos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 px-4 rounded-xl text-center transition-all duration-300 transform hover:scale-105 shadow-md"
                  >
                    💬 Telegram
                  </a>
                  <a
                    href="https://youtube.com/@GWayArgentina"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-3 px-4 rounded-xl text-center transition-all duration-300 transform hover:scale-105 shadow-md"
                  >
                    ▶️ YouTube
                  </a>
                </div>
              </div>
            </div>

            {/* Share Button */}
            <div className="mt-4">
              <button
                onClick={() => {
                  const shareText = `🌟 ¡Descubre cómo generar ingresos desde casa con productos ecológicos!%0A%0A🏠 Limpia tu hogar SIN químicos tóxicos%0A💰 Gana del 35%25 al 100%25 de comisión%0A👥 Únete a un equipo que te apoya%0A%0A👉 Más info aquí: ${window.location.href}`;
                  window.open(`https://wa.me/?text=${shareText}`, '_blank');
                }}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
              >
                📤 Compartir con un amigo en WhatsApp
              </button>
            </div>

            {/* Urgency */}
            <div className="mt-4 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl p-4 text-center border-2 border-yellow-300">
              <p className="text-yellow-800 font-semibold">
                ⚡ ¡No esperes más! Cada día que pasa es una oportunidad perdida.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Videos Section - Product Demos */}
      <div className="bg-gradient-to-br from-emerald-900 via-teal-900 to-emerald-800 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">🎬 Mira Nuestros Productos en Acción</h2>
            <p className="text-lg text-emerald-200">
              Descubre el poder de la tecnología de microfibra Greenway
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Video 1 - La Mopa */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden shadow-2xl hover:transform hover:scale-105 transition-transform duration-300">
              <video 
                className="w-full aspect-video object-cover"
                controls
                preload="metadata"
              >
                <source src="https://customer-assets.emergentagent.com/job_alianz-training-1/artifacts/nyi0kmxi_cayo%20gaseosa.mp4" type="video/mp4" />
                Tu navegador no soporta videos.
              </video>
              <div className="p-5">
                <h3 className="text-xl font-bold text-white mb-1">🧹 LA MOPA</h3>
                <p className="text-emerald-200 text-sm">Limpieza profesional para tu hogar sin químicos</p>
              </div>
            </div>
            
            {/* Video 2 - Cambiar tu Día */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden shadow-2xl hover:transform hover:scale-105 transition-transform duration-300">
              <video 
                className="w-full aspect-video object-cover"
                controls
                preload="metadata"
              >
                <source src="https://customer-assets.emergentagent.com/job_alianz-training-1/artifacts/q2zkgq3i_1000303932_286x508.mp4" type="video/mp4" />
                Tu navegador no soporta videos.
              </video>
              <div className="p-5">
                <h3 className="text-xl font-bold text-white mb-1">✨ CAMBIAR TU DÍA</h3>
                <p className="text-emerald-200 text-sm">Transforma tu rutina de limpieza diaria</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 text-center">
        <p className="text-emerald-100">© 2024 AlianzaGreen - Equipo de Líderes Greenway Global</p>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const isSociosPath = window.location.pathname === '/socios';
  const [user, setUser] = useState(null);
  const [showProspecting, setShowProspecting] = useState(!isSociosPath);
  const [showLanding, setShowLanding] = useState(isSociosPath);
  const [checkingSession, setCheckingSession] = useState(true);

  // Check for existing session on app load
  useEffect(() => {
    const savedUser = localStorage.getItem('alianzgreen_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setShowProspecting(false);
        setShowLanding(false);
      } catch (e) {
        localStorage.removeItem('alianzgreen_user');
      }
    }
    setCheckingSession(false);
  }, []);

  const handleGoToApp = () => {
    // Navigate to the dedicated associates area
    window.location.href = '/socios';
  };

  const handleStartJourney = () => {
    setShowLanding(false);
  };

  const handleLogin = (userData) => {
    // Save to localStorage
    localStorage.setItem('alianzgreen_user', JSON.stringify(userData));
    setUser(userData);
    setShowLanding(false);
    setShowProspecting(false);
  };

  // Check if we're on the admin page
  const isAdminPage = window.location.pathname === '/admin';

  const renderContent = () => {
    if (isAdminPage) {
      return <AdminPanel />;
    }

    // Show loading while checking session
    if (checkingSession) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando...</p>
          </div>
        </div>
      );
    }

    // Show prospecting landing first (for new visitors/prospects)
    if (showProspecting && !user) {
      return <ProspectingLanding onGoToApp={handleGoToApp} />;
    }

    // Show training app landing (for associates)
    if (showLanding && !user) {
      return <LandingPage onStartJourney={handleStartJourney} />;
    }

    // Show login form
    if (!user) {
      return <LoginForm onLogin={handleLogin} />;
    }

    return <TrainingApp user={user} />;
  };

  return (
    <>
      <Toaster richColors position="top-center" />
      {renderContent()}
    </>
  );
}

export default App;